import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Stage, Layer, Rect, Circle, Text, Group } from 'react-konva';
import { API_URL, LOCAL_API_URL, LIVE_API_URL } from './Config';
import arrowSvg from './assets/arrow.svg';
import './QuickPreview.css';
import './assets/fonts/fonts.css';
import BulkRenderModal from './components/BulkRenderModal';
import BulkRenderSuccessModal from './components/BulkRenderSuccessModal';
import notificationSound, { playLoopingBuzzer, stopLoopingBuzzer } from './utils/notificationSound';

// Function to check if we're on localhost with local API
const isLocalDevelopment = () => {
  const isLocalhost = window.location.hostname === 'localhost' && window.location.port === '3000';
  const isLocalAPI = API_URL === 'http://localhost:8000';
  return isLocalhost && isLocalAPI;
};

function QuickPreviewPage() {
  const { projectId, itemId } = useParams();
  const navigate = useNavigate();

  // Check if running on localhost to conditionally show/hide UI elements
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  // Language names for display
  const languageNames = {
    es: 'Spanish',
    fr: 'French',
    de: 'German',
    zh: 'Chinese (Simplified)',
    ja: 'Japanese',
    ar: 'Arabic',
    hi: 'Hindi',
    pt: 'Portuguese',
    ru: 'Russian',
    it: 'Italian',
    ko: 'Korean',
    nl: 'Dutch',
    sv: 'Swedish',
    tr: 'Turkish',
    ta: 'Tamil',
    te: 'Telugu',
    id: 'Indonesian',
  };
  
  // State management
  const [project, setProject] = useState(null);
  const [timelineItems, setTimelineItems] = useState([]);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem('selectedLanguage') || 'original');
  const [renderSpeed, setRenderSpeed] = useState(localStorage.getItem('renderSpeed') || 'fast');
  const [testMode, setTestMode] = useState(localStorage.getItem('testMode') === 'true' || false);
  const [isGeneratingVideos, setIsGeneratingVideos] = useState(false);
  const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0 });
  const [cumulativeTimeRanges, setCumulativeTimeRanges] = useState(new Map());
  const [isGeneratingConfig, setIsGeneratingConfig] = useState(false);
  const [configAvailability, setConfigAvailability] = useState(new Map());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingConfig, setIsDeletingConfig] = useState(false);
  const [isBulkRendering, setIsBulkRendering] = useState(false);
  const [bulkRenderProgress, setBulkRenderProgress] = useState({ current: 0, total: 0, currentLanguage: '' });
  const [suppressAlerts, setSuppressAlerts] = useState(false);
  const [showBulkGenModal, setShowBulkGenModal] = useState(false);
  const [isBulkGenerating, setIsBulkGenerating] = useState(false);
  const [bulkGenProgress, setBulkGenProgress] = useState({ step: '', current: 0, total: 0, currentLanguage: '' });
  const [selectedBulkLanguages, setSelectedBulkLanguages] = useState([]);
  const [selectedVoiceModel, setSelectedVoiceModel] = useState('elevenlabs');
  const [showBulkRenderModal, setShowBulkRenderModal] = useState(false);
  const [showBulkSuccessModal, setShowBulkSuccessModal] = useState(false);
  const [bulkSuccessData, setBulkSuccessData] = useState(null);

  // Media sync function (adapted from ProjectListPage)
  const handleSyncMedia = async () => {
    try {
      console.log('🔄 Starting media sync for project', projectId);
      console.log(`📡 Making request to: ${LOCAL_API_URL}/sync-media/${projectId}`);

      const response = await fetch(`${LOCAL_API_URL}/sync-media/${projectId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.detail || `HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Media sync completed:', result);

      const stats = result.statistics || {};
      const downloaded = stats.downloaded_new || 0;
      const existing = stats.updated_existing || 0;
      const failed = stats.failed || 0;
      const total = stats.total_scanned || 0;

      return {
        success: true,
        stats: { downloaded, existing, failed, total },
        projectSync: result.project_sync?.project_synced || false
      };

    } catch (err) {
      console.error('❌ Media sync error:', err);
      return {
        success: false,
        error: err.message
      };
    }
  };

  // Live composition state
  const [itemConfigurations, setItemConfigurations] = useState(new Map());
  const [currentVideoClips, setCurrentVideoClips] = useState([]);
  const [currentAudioClips, setCurrentAudioClips] = useState([]);
  const [currentShapeLayers, setCurrentShapeLayers] = useState([]);
  const [currentTextLayers, setCurrentTextLayers] = useState([]);
  const [canvasReady, setCanvasReady] = useState(false);
  const [effectiveDuration, setEffectiveDuration] = useState(0);
  const [arrowImage, setArrowImage] = useState(null);
  
  // Helper function to get display text for language
  const getLanguageDisplayText = (lang) => {
    if (lang === 'original') return 'Original';
    return languageNames[lang] || lang;
  };

  // Helper function to detect Arabic text
  const isArabicText = (text) => {
    if (!text) return false;
    // Arabic Unicode range: U+0600 to U+06FF
    const arabicRegex = /[\u0600-\u06FF]/;
    return arabicRegex.test(text);
  };

  // Helper function to get appropriate font family based on text content
  const getFontFamily = (text, currentLanguage, defaultFont = 'Arial') => {
    // Check if current language is Arabic or if text contains Arabic characters
    if (currentLanguage === 'ar' || isArabicText(text)) {
      return 'Noto Sans Arabic, Arial Unicode MS, sans-serif';
    }
    return defaultFont;
  };
  
  // Get available languages from timeline items
  const getAvailableLanguages = () => {
    const languages = new Set(['original']);
    timelineItems.forEach(item => {
      if (item.translations) {
        item.translations.forEach(translation => {
          languages.add(translation.language_code);
        });
      }
    });
    const availableLanguages = Array.from(languages);
    console.log('🔍 Available languages detected:', availableLanguages);
    return availableLanguages;
  };

  // Refs
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const timeoutRef = useRef(null);
  const canvasRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  
  // Load arrow image
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setArrowImage(img);
      console.log('Arrow image loaded for Quick Preview');
    };
    img.src = arrowSvg;
  }, []);

  // Save render speed and language to localStorage
  useEffect(() => {
    localStorage.setItem('renderSpeed', renderSpeed);
  }, [renderSpeed]);

  useEffect(() => {
    localStorage.setItem('selectedLanguage', selectedLanguage);
  }, [selectedLanguage]);

  useEffect(() => {
    localStorage.setItem('testMode', testMode.toString());
  }, [testMode]);
  
  // Check for page focus to auto-reload configurations
  useEffect(() => {
    const handleFocus = () => {
      console.log('Page gained focus - checking for updated configurations...');
      if (timelineItems.length > 0) {
        loadAllConfigurations(timelineItems, true);
      }
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [timelineItems, selectedLanguage]);
  
  // Load project and timeline data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch project data
        const projectResponse = await fetch(`${API_URL}/projects/${projectId}`);
        if (!projectResponse.ok) {
          throw new Error('Failed to fetch project');
        }
        const projectData = await projectResponse.json();
        setProject(projectData);
        
        // Fetch timeline items
        const timelineResponse = await fetch(`${API_URL}/projects/${projectId}/timeline-items`);
        if (!timelineResponse.ok) {
          throw new Error('Failed to fetch timeline items');
        }
        const timelineData = await timelineResponse.json();
        setTimelineItems(timelineData);
        
        // If itemId is provided in URL, find and set the correct index
        if (itemId) {
          const itemIndex = timelineData.findIndex(item => item.id.toString() === itemId);
          if (itemIndex !== -1) {
            console.log(`Setting currentItemIndex to ${itemIndex} for itemId ${itemId}`);
            setCurrentItemIndex(itemIndex);
          } else {
            console.warn(`Timeline item with ID ${itemId} not found`);
          }
        }
        
        // Load configurations for all timeline items
        await loadAllConfigurations(timelineData);
        
      } catch (err) {
        console.error('Error loading preview data:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [projectId]);
  
  // Load configurations for all timeline items
  const loadAllConfigurations = useCallback(async (items, forceReload = false) => {
    const configs = new Map();
    
    for (const item of items) {
      try {
        console.log(`Loading configuration for item ${item.id}${forceReload ? ' (force reload)' : ''}`);
        // Add cache-busting parameter when force reloading
        const cacheParam = forceReload ? `&_t=${Date.now()}` : '';
        const url = `${API_URL}/timeline-items/${item.id}/clip-config?language_code=${selectedLanguage}${cacheParam}`;
        console.log(`Fetching from URL: ${url}`);
        
        const response = await fetch(url, {
          // Add headers to prevent caching when force reloading
          headers: forceReload ? {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          } : {}
        });
        
        if (response.ok) {
          const configData = await response.json();
          console.log(`Raw response for item ${item.id}:`, configData);
          
          if (configData.has_custom_config && configData.configuration?.timeline) {
            configs.set(item.id, configData.configuration.timeline);
            console.log(`Loaded configuration for item ${item.id}:`, configData.configuration.timeline);
            
            // Log shapes specifically for clip 4
            if (item.id === 4 || item.id === '4') {
              console.log(`=== CLIP 4 DEBUG ===`);
              console.log(`Clip 4 full config:`, JSON.stringify(configData.configuration.timeline, null, 2));
              console.log(`Clip 4 shapes:`, configData.configuration.timeline.shapeLayers);
              console.log(`Clip 4 shapes count:`, configData.configuration.timeline.shapeLayers?.length || 0);
              console.log(`==================`);
            }
          } else {
            console.log(`No custom configuration for item ${item.id}, will use default`);
          }
        } else {
          console.log(`Failed to load configuration for item ${item.id}`);
        }
      } catch (error) {
        console.error(`Error loading configuration for item ${item.id}:`, error);
      }
    }
    
    setItemConfigurations(configs);
    console.log(`Loaded ${configs.size} configurations${forceReload ? ' (force reload)' : ''}`);
  }, [selectedLanguage]);
  
  // Function to reload configurations (can be called manually)
  const reloadConfigurations = useCallback(() => {
    console.log('Manually reloading configurations...');
    if (timelineItems.length > 0) {
      loadAllConfigurations(timelineItems, true);
    }
  }, [timelineItems, loadAllConfigurations]);

  
  // Get generated video source for current language using timeline item ID
  const getGeneratedVideoSource = useCallback((item) => {
    // New naming convention: {language}_{itemId}.mp4 or {itemId}.mp4 for original
    if (selectedLanguage === 'original') {
      return `/generated_videos/${item.id}.mp4`;
    } else {
      return `/generated_videos/${selectedLanguage}_${item.id}.mp4`;
    }
  }, [selectedLanguage]);

  // Get audio source for current language
  const getAudioSource = useCallback((item) => {
    // Intro/outro videos use their built-in audio, not separate audio files
    if (item.type === 'intro' || item.type === 'outro') {
      return null;
    }
    
    if (selectedLanguage === 'original') {
      return item.scene_audio;
    } else {
      const translation = item.translations?.find(t => t.language_code === selectedLanguage);
      return translation?.translated_audio_path || item.scene_audio;
    }
  }, [selectedLanguage]);

  
  // Get current timeline item
  const currentItem = timelineItems[currentItemIndex];
  
  // Check if item has saved configuration (edited)
  const hasCustomConfig = useCallback((item) => {
    return itemConfigurations.has(item.id);
  }, [itemConfigurations]);

  // Check if item has configuration for the currently selected language
  const hasConfigForCurrentLanguage = useCallback((item) => {
    // The itemConfigurations map is populated based on selectedLanguage,
    // so if an item is in the map, it has config for the current language
    return itemConfigurations.has(item.id);
  }, [itemConfigurations]);
  
  // Delete existing pre-rendered video for a timeline item
  const deletePreRenderedVideo = useCallback(async (item) => {
    const filename = selectedLanguage === 'original' 
      ? `${item.id}.mp4`
      : `${selectedLanguage}_${item.id}.mp4`;
    
    try {
      // Check if the video exists first
      const checkResponse = await fetch(`${API_URL}/generated_videos/${filename}`, { method: 'HEAD' });
      
      if (checkResponse.ok) {
        console.log(`🗑️ Attempting to delete existing pre-rendered video for item ${item.id}: ${filename}`);
        console.log(`ℹ️ Note: Video will be overwritten during generation, so deletion is a safety measure only`);
        
        // Try to use the existing project videos delete endpoint
        const videoPath = `/generated_videos/${filename}`;
        const deleteResponse = await fetch(`${API_URL}/projects/${projectId}/videos`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ video_path: videoPath })
        });
        
        if (deleteResponse.ok) {
          console.log(`✅ Successfully deleted existing video for item ${item.id}`);
          return true;
        } else {
          const errorText = await deleteResponse.text();
          console.warn(`⚠️ Could not delete existing video for item ${item.id}: HTTP ${deleteResponse.status} - ${errorText}`);
          console.log(`ℹ️ This is expected for timeline item videos due to backend security restrictions. Video will be overwritten during generation.`);
          // Return true anyway since the video will be overwritten during generation
          return true;
        }
      } else {
        console.log(`ℹ️ No existing video to delete for item ${item.id}`);
        return true; // No video exists, so "deletion" is successful
      }
    } catch (error) {
      console.warn(`⚠️ Error checking/deleting video for item ${item.id}:`, error);
      console.log(`ℹ️ Continuing anyway since video will be overwritten during generation`);
      return true; // Don't block generation due to deletion errors
    }
  }, [selectedLanguage, projectId]);

  // Generate videos for all edited clips
  const handleGenerateVideos = useCallback(async () => {
    if (isGeneratingVideos) return;

    // Find all timeline items that have custom configurations (edited clips)
    const editedItems = timelineItems.filter(item => hasCustomConfig(item));
    
    if (editedItems.length === 0) {
      alert('No edited clips found. Please edit clips first before generating videos.');
      return;
    }

    setIsGeneratingVideos(true);
    setGenerationProgress({ current: 0, total: editedItems.length });

    console.log(`🎬 Starting optimized bulk video generation for ${editedItems.length} edited clips`);
    console.log(`♻️ Will reuse existing videos and only generate missing ones`);
    console.log(`🚀 NEW CODE PATH: Using bulk generation endpoint instead of individual clips`);

    try {
      // First, fetch project and timeline data from live API
      console.log(`📡 Fetching project data from live API for project ${projectId}`);
      setGenerationProgress({ current: 1, total: 3, phase: 'Fetching project data...' });
      
      const [projectResponse, timelineResponse] = await Promise.all([
        fetch(`${API_URL}/projects/${projectId}`),
        fetch(`${API_URL}/projects/${projectId}/timeline`)
      ]);
      
      if (!projectResponse.ok || !timelineResponse.ok) {
        throw new Error('Failed to fetch project data from live API');
      }
      
      const projectData = await projectResponse.json();
      const timelineData = await timelineResponse.json();
      
      console.log(`✅ Fetched project data: ${projectData.title}, Timeline items: ${timelineData.length}`);
      
      // Pass data directly to render endpoint without database storage
      console.log(`🚀 Calling local render API with live data...`);
      setGenerationProgress({ current: 2, total: 2, phase: 'Rendering videos...' });
      
      // Use the optimized bulk generation endpoint that reuses existing videos
      const queryParams = new URLSearchParams({
        force_regenerate: 'false', // Explicitly set to reuse existing videos
        test_mode: testMode.toString()
      });
      
      if (selectedLanguage && selectedLanguage !== 'original') {
        queryParams.set('target_language_code', selectedLanguage);
      }
      
      console.log(`🎯 Test mode enabled: ${testMode}`);
      console.log(`🚀 Calling local render API: /projects/${projectId}/generate-video-with-data?${queryParams}`);
      
      const response = await fetch(`${LOCAL_API_URL}/projects/${projectId}/generate-video-with-data?${queryParams}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          project: projectData,
          timeline: timelineData
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log(`📊 Video generation response:`, result);

      // Check if the generation was actually successful
      if (result.success === false) {
        throw new Error(result.error || 'Video generation failed');
      }

      // Update progress to completion
      setGenerationProgress({ current: 2, total: 2, phase: 'Complete!', completed: true });

      const clipInfo = result.total_clips_used
        ? `\n\nUsed ${result.total_clips_used} clips`
        : `\n\nProcessed ${editedItems.length} clips`;

      if (!suppressAlerts) {
        alert(`🎉 Success!\n\nTimeline video generated successfully for "${project?.title}" in ${selectedLanguage.toUpperCase()} language.${clipInfo}\n\nDuration: ${result.final_duration?.toFixed(1) || 'N/A'}s`);
      }

      // Skip the old individual verification and full timeline generation
      setIsGeneratingVideos(false);
      setGenerationProgress({ current: 0, total: 0 });
      return;
      
    } catch (error) {
      console.error(`❌ Bulk video generation failed:`, error);
      
      // Check if this is a clip validation error
      if (error.message && error.message.includes('Failed to generate') && error.message.includes('clips')) {
        // Extract clip IDs from error message for better user experience
        const clipMatch = error.message.match(/Failed to generate \d+ clips?: (.+)/);
        const clipIds = clipMatch ? clipMatch[1] : 'unknown';

        if (!suppressAlerts) {
          alert(`❌ Video Generation Failed!\n\nThe following clips could not be generated due to missing audio files or invalid configurations:\n\nFailed Clips: ${clipIds}\n\nPlease ensure all required audio files are available for the selected language, or edit these specific clips (${clipIds}) in the clip-editor to fix their configurations.`);
        }
      } else if (error.message && error.message.includes('incomplete clips')) {
        if (!suppressAlerts) {
          alert(`❌ Video Generation Failed!\n\nSome clips are missing or have errors.\n\nError: ${error.message}\n\nPlease ensure all clips are properly configured before generating the timeline video.`);
        }
      } else {
        if (!suppressAlerts) {
          alert(`❌ Video Generation Failed!\n\nError: ${error.message}\n\nPlease check the console for more details.`);
        }
      }
      
      setIsGeneratingVideos(false);
      setGenerationProgress({ current: 0, total: 0 });
      return;
    }

    // This code is no longer reached due to early returns above
    console.log('⚠️ This code should not be reached - check logic above');

  }, [isGeneratingVideos, timelineItems, hasCustomConfig, itemConfigurations, projectId, project, selectedLanguage, deletePreRenderedVideo]);

  // Check if a pre-rendered video file actually exists on the server
  const checkPreRenderedVideoExists = useCallback(async (item) => {
    const videoPath = selectedLanguage === 'original' 
      ? `/generated_videos/${item.id}.mp4`
      : `/generated_videos/${selectedLanguage}_${item.id}.mp4`;
    
    try {
      const response = await fetch(`${API_URL}${videoPath}`, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      console.warn(`Failed to check if video exists for item ${item.id}:`, error);
      return false;
    }
  }, [selectedLanguage]);

  // Check if configuration exists for the current timeline item and selected language
  const checkConfigurationExists = useCallback(async (itemId, language) => {
    try {
      const response = await fetch(`${API_URL}/timeline-items/${itemId}/clip-config?language_code=${language}`);
      const data = await response.json();
      return data.has_custom_config || data.configuration !== null;
    } catch (error) {
      console.warn(`Failed to check configuration for item ${itemId} in language ${language}:`, error);
      return false;
    }
  }, []);

  // Generate configurations for ALL timeline items in the selected language
  const handleGenerateConfiguration = useCallback(async () => {
    if (selectedLanguage === 'original') {
      alert('Cannot generate configurations for original language.');
      return;
    }
    
    console.log(`🔄 GENERATE ALL CONFIGURATIONS for language: ${selectedLanguage}`);
    
    setIsGeneratingConfig(true);
    try {
      let generatedCount = 0;
      let skippedCount = 0;
      let failedCount = 0;
      const errors = [];

      // Generate configurations for all timeline items that don't have them (skip outro items)
      for (const item of timelineItems) {
        // Skip outro items - they don't need configurations
        if (item.type === 'outro') {
          skippedCount++;
          console.log(`⏭️ Skipped outro item ${item.id} - outro configurations not required`);
          continue;
        }

        try {
          const response = await fetch(`${API_URL}/timeline-items/${item.id}/generate-config/${selectedLanguage}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            }
          });

          if (response.ok) {
            generatedCount++;
            console.log(`✅ Generated configuration for item ${item.id} (${item.type}) in language ${selectedLanguage}`);
          } else {
            const errorData = await response.json();
            if (response.status === 400 && errorData.detail?.includes('already exists')) {
              // Configuration already exists - this is expected, not a failure
              skippedCount++;
              console.log(`⏭️ Skipped item ${item.id} - configuration already exists`);
            } else {
              failedCount++;
              errors.push(`Item ${item.id} (${item.type}): ${errorData.detail}`);
              console.error(`❌ Failed to generate config for item ${item.id}: ${errorData.detail}`);
            }
          }
        } catch (error) {
          failedCount++;
          errors.push(`Item ${item.id} (${item.type}): ${error.message}`);
          console.error(`❌ Error generating config for item ${item.id}:`, error);
        }
      }

      // Update configuration availability for all items
      setConfigAvailability(prev => {
        const newMap = new Map(prev);
        timelineItems.forEach(item => {
          const key = `${item.id}-${selectedLanguage}`;
          // Set to true for items that were generated or already existed
          newMap.set(key, true);
        });
        return newMap;
      });

      // Reload all configurations to reflect changes
      await loadAllConfigurations(timelineItems, true);

      // Show results
      let message = '';
      if (generatedCount > 0) {
        message += `✅ Generated ${generatedCount} new configurations for ${selectedLanguage} language!`;
      }
      if (skippedCount > 0) {
        message += `\n⏭️ Skipped ${skippedCount} items (configurations already existed)`;
      }
      if (failedCount > 0) {
        message += `\n❌ Failed to generate ${failedCount} configurations`;
      }
      
      if (generatedCount === 0 && skippedCount === 0 && failedCount === 0) {
        message = `No items found to generate configurations for ${selectedLanguage} language.`;
      }

      alert(message);

      if (errors.length > 0) {
        console.error('Generation errors:', errors);
      }
    } catch (error) {
      console.error('Error generating configurations:', error);
      alert(`Failed to generate configurations: ${error.message}`);
    } finally {
      setIsGeneratingConfig(false);
    }
  }, [timelineItems, selectedLanguage, loadAllConfigurations]);

  // Delete ALL configurations for ALL timeline items in the selected language
  const handleDeleteConfiguration = useCallback(async () => {
    if (selectedLanguage === 'original') {
      alert('Cannot delete original language configurations.');
      return;
    }
    
    console.log(`🗑️ DELETE ALL CONFIGURATIONS for language: ${selectedLanguage}`);
    
    setIsDeletingConfig(true);
    try {
      let deletedCount = 0;
      let failedCount = 0;
      const errors = [];

      // Delete configurations for all timeline items that have them
      for (const item of timelineItems) {
        try {
          const response = await fetch(`${API_URL}/timeline-items/${item.id}/clip-config/${selectedLanguage}`, {
            method: 'DELETE'
          });

          if (response.ok) {
            deletedCount++;
            console.log(`✅ Deleted configuration for item ${item.id} in language ${selectedLanguage}`);
          } else {
            // 404 is expected for items without configurations, don't count as failure
            const errorData = await response.json();
            if (response.status !== 404) {
              failedCount++;
              errors.push(`Item ${item.id}: ${errorData.detail}`);
            }
          }
        } catch (error) {
          failedCount++;
          errors.push(`Item ${item.id}: ${error.message}`);
        }
      }

      // Update configuration availability for all items
      setConfigAvailability(prev => {
        const newMap = new Map(prev);
        timelineItems.forEach(item => {
          const key = `${item.id}-${selectedLanguage}`;
          newMap.set(key, false);
        });
        return newMap;
      });

      // Reload configurations to reflect deletions
      await loadAllConfigurations(timelineItems, true);

      setShowDeleteModal(false);
      
      if (deletedCount > 0) {
        alert(`Successfully deleted ${deletedCount} configurations for ${selectedLanguage} language!${failedCount > 0 ? `\n\n${failedCount} items failed to delete.` : ''}`);
      } else {
        alert(`No configurations found to delete for ${selectedLanguage} language.`);
      }

      if (errors.length > 0) {
        console.error('Delete errors:', errors);
      }
    } catch (error) {
      console.error('Error deleting configurations:', error);
      alert(`Failed to delete configurations: ${error.message}`);
    } finally {
      setIsDeletingConfig(false);
    }
  }, [timelineItems, selectedLanguage, loadAllConfigurations]);

  // Check configuration availability for all items when language changes
  useEffect(() => {
    const checkAllItemsConfig = async () => {
      if (selectedLanguage !== 'original' && timelineItems.length > 0) {
        const configChecks = timelineItems.map(async (item) => {
          const exists = await checkConfigurationExists(item.id, selectedLanguage);
          return { itemId: item.id, exists };
        });

        const results = await Promise.all(configChecks);
        
        setConfigAvailability(prev => {
          const newMap = new Map(prev);
          results.forEach(({ itemId, exists }) => {
            const key = `${itemId}-${selectedLanguage}`;
            newMap.set(key, exists);
          });
          return newMap;
        });
      }
      
      // Also reload all configurations to get the correct duration data for the new language
      if (timelineItems.length > 0) {
        await loadAllConfigurations(timelineItems, true);
      }
    };

    checkAllItemsConfig();
  }, [timelineItems, selectedLanguage, checkConfigurationExists, loadAllConfigurations]);

  // Generate the full timeline video using a mix of pre-rendered clips and live rendering
  const handleGenerateFullTimeline = useCallback(async () => {
    console.log(`🎬 Starting full timeline generation verification for project ${projectId} in ${selectedLanguage}`);
    
    try {
      // Find all edited clips that should have pre-rendered videos
      const editedItems = timelineItems.filter(item => hasCustomConfig(item));
      
      if (editedItems.length === 0) {
        console.log(`ℹ️ No edited clips found, skipping full timeline generation`);
        return;
      }

      // Update progress for verification phase
      setGenerationProgress({ 
        current: editedItems.length + 1, 
        total: editedItems.length + 2,
        phase: `Verifying ${editedItems.length} pre-rendered clips...`
      });
      
      // Verify that all edited clips have their pre-rendered videos available
      console.log(`🔍 Verifying ${editedItems.length} pre-rendered videos exist...`);
      
      const videoCheckPromises = editedItems.map(async (item) => {
        const exists = await checkPreRenderedVideoExists(item);
        return { item, exists };
      });

      const videoCheckResults = await Promise.all(videoCheckPromises);
      const missingVideos = videoCheckResults.filter(result => !result.exists);

      if (missingVideos.length > 0) {
        const missingIds = missingVideos.map(result => result.item.id);
        const errorMessage = `❌ Cannot generate full timeline: Pre-rendered videos missing for clips: ${missingIds.join(', ')}`;
        console.error(errorMessage);
        alert(`Full timeline generation failed!\n\nMissing pre-rendered videos for clips: ${missingIds.join(', ')}\n\nPlease ensure all individual clip videos are generated successfully before generating the full timeline.`);
        
        // Reset progress on failure
        setGenerationProgress({ current: 0, total: 0 });
        return;
      }

      console.log(`✅ All ${editedItems.length} pre-rendered videos verified. Proceeding with full timeline generation...`);
      
      // Update progress for final generation phase
      setGenerationProgress({ 
        current: editedItems.length + 2, 
        total: editedItems.length + 2,
        phase: 'Generating final timeline video...'
      });

      // All pre-rendered videos exist, proceed with full timeline generation
      const fullTimelineData = {
        projectInfo: {
          projectId: parseInt(projectId),
          timelineItemId: 0, // Full timeline
          title: project?.title || 'Untitled Project',
          description: project?.description || '',
          originalVideoUrl: project?.videoUrl || '',
          originalVideoFile: project?.videoUrl ? `${API_URL}${project.videoUrl}` : '',
          totalDuration: timelineItems.reduce((total, item) => total + (item.endTime - item.startTime), 0),
          language: selectedLanguage,
          createdAt: new Date().toISOString(),
          suggestedOutputName: `project_${projectId}_full_timeline_${selectedLanguage}_${new Date().toISOString().replace(/[:.]/g, '-')}.mp4`
        },
        sourceMedia: {
          video: {
            file: project?.videoUrl ? `${API_URL}${project.videoUrl}` : '',
            startTime: 0,
            endTime: project?.duration || 0,
            duration: project?.duration || 0
          },
          audio: null // Will be determined per timeline item
        },
        timeline: {
          duration: timelineItems.reduce((total, item) => total + (item.endTime - item.startTime), 0),
          timelineItems: timelineItems.map((item, index) => {
            const hasPreRendered = hasCustomConfig(item);
            const config = itemConfigurations.get(item.id);
            
            return {
              id: item.id,
              type: item.type,
              startTime: item.startTime,
              endTime: item.endTime,
              duration: item.endTime - item.startTime,
              orderIndex: index,
              hasPreRenderedVideo: hasPreRendered,
              preRenderedVideoPath: hasPreRendered ? (selectedLanguage === 'original' 
                ? `/generated_videos/${item.id}.mp4`
                : `/generated_videos/${selectedLanguage}_${item.id}.mp4`) : null,
              audioPath: getAudioSource(item),
              customConfiguration: config ? {
                videoTrack: config.videoTrack || [],
                audioTrack: config.audioTrack || [],
                shapeLayers: config.shapeLayers || [],
                textLayers: config.textLayers || []
              } : null,
              // For items without pre-rendered video, include raw timeline item data
              rawTimelineItem: !hasPreRendered ? {
                timestamp: item.timestamp,
                scene_text: item.scene_text,
                rawText: item.rawText,
                scene_audio: item.scene_audio,
                translations: item.translations
              } : null
            };
          })
        },
        renderSettings: {
          outputDuration: timelineItems.reduce((total, item) => total + (item.endTime - item.startTime), 0),
          language: selectedLanguage,
          timelineItems: timelineItems.length,
          preRenderedItems: timelineItems.filter(item => hasCustomConfig(item)).length,
          liveRenderItems: timelineItems.filter(item => !hasCustomConfig(item)).length,
          outputPath: selectedLanguage === 'original' 
            ? `/generated_videos/project_${projectId}_full.mp4`
            : `/generated_videos/project_${projectId}_full_${selectedLanguage}.mp4`,
          outputMetadata: {
            projectTitle: project?.title || 'Untitled Project',
            projectId: projectId,
            language: selectedLanguage,
            generatedAt: new Date().toISOString(),
            totalTimelineItems: timelineItems.length,
            preRenderedClips: timelineItems.filter(item => hasCustomConfig(item)).length
          }
        },
        metadata: {
          version: "1.0",
          language: selectedLanguage,
          fullTimeline: true,
          projectId: projectId,
          generationType: "hybrid", // Mix of pre-rendered and live rendering
          generatedAt: new Date().toISOString()
        }
      };

      console.log(`📊 Full timeline data prepared:`, {
        totalItems: timelineItems.length,
        preRendered: timelineItems.filter(item => hasCustomConfig(item)).length,
        liveRender: timelineItems.filter(item => !hasCustomConfig(item)).length,
        totalDuration: fullTimelineData.timeline.duration
      });

      // Call the simple concatenation endpoint (no body needed)
      console.log(`🔗 Using simple concatenation of pre-rendered clips`);
      const response = await fetch(`${LOCAL_API_URL}/projects/${projectId}/generate-video-simple-concat?target_language_code=${selectedLanguage}&test_mode=${testMode}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log(`✅ Full timeline video generated successfully:`, result);
      
      // Update progress to show completion
      setGenerationProgress({ 
        current: editedItems.length + 2, 
        total: editedItems.length + 2,
        phase: 'Video generation completed!',
        completed: true
      });
      
      // Show detailed success message
      const clipInfo = result.cleanup ? 
        `\n\nGenerated from ${result.clips_concatenated} clips (${result.cleanup.summary})` :
        `\n\nGenerated from ${result.clips_concatenated || 'multiple'} clips`;
      
      alert(`🎉 Success!\n\nFull timeline video generated successfully for "${project?.title}" in ${selectedLanguage.toUpperCase()} language.${clipInfo}\n\nDuration: ${result.final_duration?.toFixed(1)}s`);

    } catch (error) {
      console.error(`❌ Failed to generate full timeline video:`, error);
      alert(`Failed to generate full timeline video: ${error.message}`);
      
      // Reset progress on error
      setGenerationProgress({ current: 0, total: 0 });
    }
  }, [projectId, timelineItems, project, selectedLanguage, itemConfigurations, hasCustomConfig, getAudioSource, checkPreRenderedVideoExists]);
  
  // Bulk render for all available languages (including original) - Frontend Queue System
  const handleBulkRender = useCallback(async () => {
    console.log('🎬 Opening bulk render modal');
    setShowBulkRenderModal(true);
  }, []);

  // Actual bulk render function (called from modal)
  const executeBulkRender = useCallback(async (syncMedia = false) => {
    console.log('🎬 Starting bulk render for all available languages');
    const languagesToRender = getAvailableLanguages(); // Includes 'original' + all other languages

    setShowBulkRenderModal(false);

    // Sync media first if requested
    if (syncMedia) {
      console.log('🔄 Syncing media before bulk render...');
      const syncResult = await handleSyncMedia();

      if (!syncResult.success) {
        console.error(`❌ Media sync failed: ${syncResult.error}. Cannot proceed with bulk render.`);
        // Set bulk render as failed state instead of showing alert
        setIsBulkRendering(false);
        setBulkRenderProgress({ current: 0, total: 0, currentLanguage: '' });
        return;
      }

      console.log('✅ Media sync completed successfully');
      // Log sync results to console instead of alert
      const { stats } = syncResult;
      console.log(`✅ Media sync completed! Downloaded: ${stats.downloaded}, Existing: ${stats.existing}, Failed: ${stats.failed}, Total: ${stats.total}`);
    }

    // Initialize bulk render queue in localStorage
    const bulkRenderQueue = languagesToRender.map((language, index) => ({
      language,
      displayName: language === 'original' ? 'Original' : getLanguageDisplayText(language),
      status: 'pending', // 'pending', 'rendering', 'completed', 'failed'
      order: index,
      result: null,
      error: null,
      startedAt: null,
      completedAt: null
    }));

    const bulkRenderSession = {
      projectId: projectId,
      sessionId: `bulk-render-${Date.now()}`,
      startedAt: new Date().toISOString(),
      originalLanguageSelection: selectedLanguage,
      queue: bulkRenderQueue,
      currentIndex: 0,
      totalLanguages: languagesToRender.length,
      completedCount: 0,
      failedCount: 0
    };

    // Store in localStorage
    localStorage.setItem('bulkRenderSession', JSON.stringify(bulkRenderSession));
    console.log('📝 Bulk render queue initialized in localStorage:', bulkRenderSession);

    setIsBulkRendering(true);
    setSuppressAlerts(true); // Suppress individual render alerts during bulk process
    setBulkRenderProgress({ current: 0, total: languagesToRender.length, currentLanguage: '' });

    // Start processing the queue
    setTimeout(() => processBulkRenderQueue(), 100); // Use setTimeout to avoid circular dependency

  }, [projectId, timelineItems, project, selectedLanguage, getAvailableLanguages, getLanguageDisplayText, handleSyncMedia]);

  // Process bulk render queue one language at a time
  const processBulkRenderQueue = useCallback(async () => {
    try {
      const storedSession = localStorage.getItem('bulkRenderSession');
      if (!storedSession) {
        console.error('❌ No bulk render session found in localStorage');
        return;
      }

      let session = JSON.parse(storedSession);

      // Check if we have more languages to process
      if (session.currentIndex >= session.totalLanguages) {
        // All languages processed - show final results
        finalizeBulkRender(session);
        return;
      }

      const currentItem = session.queue[session.currentIndex];
      console.log(`🎬 Processing language ${session.currentIndex + 1}/${session.totalLanguages}: ${currentItem.displayName}`);

      // Update UI progress
      setBulkRenderProgress({
        current: session.currentIndex + 1,
        total: session.totalLanguages,
        currentLanguage: currentItem.displayName
      });

      // Update status to 'rendering'
      currentItem.status = 'rendering';
      currentItem.startedAt = new Date().toISOString();
      session.queue[session.currentIndex] = currentItem;
      localStorage.setItem('bulkRenderSession', JSON.stringify(session));

      try {
        // Temporarily switch to this language for rendering
        setSelectedLanguage(currentItem.language);

        // Get project and timeline data from live API
        console.log('📡 Fetching project and timeline data...');
        const projectResponse = await fetch(`${LIVE_API_URL}/projects/${projectId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (!projectResponse.ok) {
          throw new Error(`Failed to fetch project: ${projectResponse.status}`);
        }

        const projectData = await projectResponse.json();
        const timelineData = projectData.timeline_items || [];

        // Prepare API call
        const queryParams = new URLSearchParams({
          force_regenerate: 'false',
          test_mode: testMode.toString()
        });

        // Only set target_language_code for non-original languages
        if (currentItem.language !== 'original') {
          queryParams.set('target_language_code', currentItem.language);
        }

        console.log(`🚀 Calling render API for ${currentItem.displayName}: /projects/${projectId}/generate-video-with-data?${queryParams}`);

        // Make the render API call
        const response = await fetch(`${LOCAL_API_URL}/projects/${projectId}/generate-video-with-data?${queryParams}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            project: projectData,
            timeline: timelineData
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        console.log(`✅ Render completed successfully for ${currentItem.displayName}:`, result);

        // Update session with success
        session = JSON.parse(localStorage.getItem('bulkRenderSession'));
        session.queue[session.currentIndex].status = 'completed';
        session.queue[session.currentIndex].result = result;
        session.queue[session.currentIndex].completedAt = new Date().toISOString();
        session.completedCount++;
        session.currentIndex++;

      } catch (error) {
        console.error(`❌ Render failed for ${currentItem.displayName}:`, error);

        // Update session with failure
        session = JSON.parse(localStorage.getItem('bulkRenderSession'));
        session.queue[session.currentIndex].status = 'failed';
        session.queue[session.currentIndex].error = error.message;
        session.queue[session.currentIndex].completedAt = new Date().toISOString();
        session.failedCount++;
        session.currentIndex++;
      }

      // Save updated session
      localStorage.setItem('bulkRenderSession', JSON.stringify(session));

      // Continue with next language (recursive call)
      setTimeout(() => processBulkRenderQueue(), 1000); // Small delay between renders

    } catch (error) {
      console.error('❌ Critical error in bulk render queue processing:', error);
      finalizeBulkRender(null, error);
    }
  }, [projectId]);

  // Media sync function (download from live to local - same as ProjectListPage)
  const syncMediaToLiveServer = useCallback(async () => {
    try {
      console.log(`🔄 Starting media upload sync for project ${projectId}...`);
      console.log(`📡 Making request to: ${LOCAL_API_URL}/projects/${projectId}/sync-media`);

      const response = await fetch(`${LOCAL_API_URL}/projects/${projectId}/sync-media`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ force_upload: false })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.detail || `HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Media sync completed:', result);

      // Handle new HTTP upload response format
      if (result.success) {
        const uploaded = result.successful_uploads || 0;
        const totalFiles = result.total_files || 0;
        const failed = result.failed_files ? result.failed_files.length : 0;

        if (failed === 0) {
          return {
            success: true,
            message: `✅ Media upload sync completed successfully!\n\n📤 Uploaded: ${uploaded} files\n📊 Total files: ${totalFiles}\n🚀 All videos synced to production server!`
          };
        } else {
          return {
            success: false,
            message: `⚠️ Media sync completed with issues!\n\n📤 Uploaded: ${uploaded} files\n❌ Failed: ${failed} files\n📊 Total files: ${totalFiles}`
          };
        }
      } else {
        return {
          success: false,
          message: `❌ Media sync failed!\n\n${result.error || 'Unknown error occurred'}`
        };
      }

    } catch (err) {
      console.error('❌ Media sync error:', err);
      return {
        success: false,
        message: `❌ Media sync failed: ${err.message}`
      };
    }
  }, [projectId]);


  // Finalize bulk render process and show results
  const finalizeBulkRender = useCallback(async (session, criticalError = null) => {
    try {
      if (criticalError) {
        console.error(`Bulk render process failed: ${criticalError.message}`);
        // Set error state and show modal with error
        setBulkSuccessData({
          session: { completedCount: 0, failedCount: 1, queue: [], originalLanguageSelection: selectedLanguage },
          uploadResult: null
        });
        setShowBulkSuccessModal(true);
        return;
      }

      if (session) {
        // Restore original language selection
        setSelectedLanguage(session.originalLanguageSelection);

        console.log('📊 Final bulk render session:', session);

        let uploadResult = null;

        // If bulk render was successful and running locally, sync videos to production server
        if (session.completedCount > 0 && isLocalhost) {
          console.log('🚀 Starting automatic media sync to production server...');
          uploadResult = await syncMediaToLiveServer();
          console.log('📤 Media sync result:', uploadResult);
        } else if (session.completedCount > 0 && !isLocalhost) {
          console.log('ℹ️ Skipping media sync - not running on localhost');
        }

        // Set success data for modal
        setBulkSuccessData({
          session: session,
          uploadResult: uploadResult
        });

        // Start looping buzzer sound
        console.log('🔔 Starting completion notification sound...');
        playLoopingBuzzer(800, 1000, 0.3); // 800Hz frequency, 1 second intervals, 30% volume

        // Show success modal
        setShowBulkSuccessModal(true);
      }

      // Clean up
      localStorage.removeItem('bulkRenderSession');
      setIsBulkRendering(false);
      setSuppressAlerts(false); // Re-enable alerts after bulk process
      setBulkRenderProgress({ current: 0, total: 0, currentLanguage: '' });

    } catch (error) {
      console.error('❌ Error finalizing bulk render:', error);
      setBulkSuccessData({
        session: { completedCount: 0, failedCount: 1, queue: [], originalLanguageSelection: selectedLanguage },
        uploadResult: { success: false, message: `Finalization error: ${error.message}` }
      });
      setShowBulkSuccessModal(true);
      setIsBulkRendering(false);
      setSuppressAlerts(false); // Re-enable alerts on error
      setBulkRenderProgress({ current: 0, total: 0, currentLanguage: '' });
    }
  }, [syncMediaToLiveServer, selectedLanguage]);

  // Handle modal close and stop notification sound
  const handleBulkSuccessModalClose = useCallback(() => {
    console.log('🔇 Stopping notification sound and closing modal');
    stopLoopingBuzzer();
    setShowBulkSuccessModal(false);
    setBulkSuccessData(null);
  }, []);

  // Handle bulk generation of script, audio, and configurations using existing working APIs
  const handleBulkGeneration = useCallback(async () => {
    if (selectedBulkLanguages.length === 0) {
      alert('Please select at least one language.');
      return;
    }

    console.log(`🚀 Starting bulk generation for languages: ${selectedBulkLanguages.join(', ')}`);
    console.log(`🎤 Using voice model: ${selectedVoiceModel}`);

    setIsBulkGenerating(true);
    setShowBulkGenModal(false);
    
    const totalSteps = selectedBulkLanguages.length * 3; // 3 steps per language: script, audio, config
    let currentStep = 0;
    
    const results = [];

    try {
      for (const language of selectedBulkLanguages) {
        const languageDisplay = languageNames[language] || language;
        let languageResult = { language: languageDisplay, success: false, errors: [] };

        try {
          // Step 1: Generate AI Script using existing /projects/{id}/analyze endpoint
          currentStep++;
          setBulkGenProgress({
            step: 'Generating AI Script',
            current: currentStep,
            total: totalSteps,
            currentLanguage: languageDisplay
          });

          console.log(`📝 Generating AI script for ${languageDisplay} using existing analyze endpoint...`);
          console.log(`⏰ Script generation timeout: 15 minutes`);
          console.log(`🔄 Will retry up to 2 times if fetch fails`);

          // Add retry logic for script generation
          let scriptResult = null;
          let lastScriptError = null;
          const maxScriptRetries = 2;

          for (let attempt = 1; attempt <= maxScriptRetries; attempt++) {
            console.log(`🎯 Script generation attempt ${attempt}/${maxScriptRetries} for ${languageDisplay}`);

            const scriptController = new AbortController();
            const scriptTimeoutId = setTimeout(() => scriptController.abort(), 900000); // 15 minutes timeout

            try {
              const scriptResponse = await fetch(`${API_URL}/projects/${projectId}/analyze?target_language_code=${language}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                signal: scriptController.signal
              });

              clearTimeout(scriptTimeoutId);

              if (!scriptResponse.ok) {
                const errorData = await scriptResponse.text();
                throw new Error(`HTTP ${scriptResponse.status}: ${errorData}`);
              }

              scriptResult = await scriptResponse.json();
              console.log(`✅ Generated ${scriptResult.items_updated || 0} scripts for ${languageDisplay} (attempt ${attempt})`);
              break; // Success, exit retry loop

            } catch (scriptError) {
              clearTimeout(scriptTimeoutId);
              lastScriptError = scriptError;

              if (scriptError.name === 'AbortError') {
                console.log(`⏰ Script generation timed out after 15 minutes (attempt ${attempt})`);
                lastScriptError = new Error('Script generation timed out after 15 minutes');
              } else if (scriptError.message.includes('Failed to fetch') || scriptError.name === 'TypeError') {
                console.log(`🌐 Network/fetch error on attempt ${attempt}: ${scriptError.message}`);
                lastScriptError = new Error(`Network error: ${scriptError.message}`);
              } else {
                console.log(`❌ Script generation error on attempt ${attempt}: ${scriptError.message}`);
              }

              // If not the last attempt, wait before retrying
              if (attempt < maxScriptRetries) {
                const waitTime = 15000; // 15s wait between retries
                console.log(`⏳ Waiting ${waitTime/1000} seconds before retry...`);
                await new Promise(resolve => setTimeout(resolve, waitTime));
              }
            }
          }

          // Check if all retries failed
          if (!scriptResult) {
            throw new Error(`Script generation failed after ${maxScriptRetries} attempts: ${lastScriptError?.message || 'Unknown error'}`);
          }

          // Step 2: Generate Audio using existing /projects/{id}/generate-audio endpoint
          currentStep++;
          setBulkGenProgress({
            step: 'Generating Audio',
            current: currentStep,
            total: totalSteps,
            currentLanguage: languageDisplay
          });

          console.log(`🎵 Generating audio for ${languageDisplay} using existing audio endpoint...`);
          console.log(`⏰ Audio generation timeout: 20 minutes (ElevenLabs can be very slow)`);
          console.log(`🔄 Will retry up to 3 times if fetch fails`);

          const voiceModelParam = selectedVoiceModel === 'elevenlabs' ? 'eleven_labs' : 'google';

          // Add retry logic for audio generation with longer timeout
          let audioResult = null;
          let lastError = null;
          const maxRetries = 3;

          for (let attempt = 1; attempt <= maxRetries; attempt++) {
            console.log(`🎯 Audio generation attempt ${attempt}/${maxRetries} for ${languageDisplay}`);

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 1200000); // 20 minutes timeout

            try {
              const audioResponse = await fetch(`${API_URL}/projects/${projectId}/generate-audio?voice_model=${voiceModelParam}&target_language_code=${language}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                signal: controller.signal
              });

              clearTimeout(timeoutId);

              if (!audioResponse.ok) {
                const errorData = await audioResponse.text();
                throw new Error(`HTTP ${audioResponse.status}: ${errorData}`);
              }

              audioResult = await audioResponse.json();
              console.log(`✅ Generated ${audioResult.items_updated || 0} audio files for ${languageDisplay} (attempt ${attempt})`);
              break; // Success, exit retry loop

            } catch (audioError) {
              clearTimeout(timeoutId);
              lastError = audioError;

              if (audioError.name === 'AbortError') {
                console.log(`⏰ Audio generation timed out after 20 minutes (attempt ${attempt})`);
                lastError = new Error('Audio generation timed out after 20 minutes');
              } else if (audioError.message.includes('Failed to fetch') || audioError.name === 'TypeError') {
                console.log(`🌐 Network/fetch error on attempt ${attempt}: ${audioError.message}`);
                lastError = new Error(`Network error: ${audioError.message}`);
              } else {
                console.log(`❌ Audio generation error on attempt ${attempt}: ${audioError.message}`);
              }

              // If not the last attempt, wait before retrying
              if (attempt < maxRetries) {
                const waitTime = attempt * 30000; // 30s, 60s wait between retries
                console.log(`⏳ Waiting ${waitTime/1000} seconds before retry...`);
                await new Promise(resolve => setTimeout(resolve, waitTime));
              }
            }
          }

          // Check if all retries failed
          if (!audioResult) {
            throw new Error(`Audio generation failed after ${maxRetries} attempts: ${lastError?.message || 'Unknown error'}`);
          }

          // Wait longer for audio files to be fully written and accessible (especially for ElevenLabs)
          console.log(`⏳ Waiting 10 seconds for audio files to be fully processed and written to disk...`);
          await new Promise(resolve => setTimeout(resolve, 10000));

          // Step 3: Generate Configurations using existing /timeline-items/{id}/generate-config/{language} endpoint
          currentStep++;
          setBulkGenProgress({
            step: 'Generating Configurations',
            current: currentStep,
            total: totalSteps,
            currentLanguage: languageDisplay
          });

          console.log(`⚙️ Starting configuration generation for ${languageDisplay}...`);
          console.log(`📊 Total timeline items to process: ${timelineItems.length}`);

          let configSuccessCount = 0;
          let configAttemptCount = 0;
          for (const item of timelineItems) {
            configAttemptCount++;
            console.log(`🔧 Processing config ${configAttemptCount}/${timelineItems.length} for item ${item.id} (${item.type}) in ${languageDisplay}`);
            // Skip outro items proactively - they don't need configurations
            if (item.type === 'outro') {
              configSuccessCount++;
              console.log(`⏭️ Skipped outro item ${item.id} - outro configurations not required`);
              continue;
            }

            try {
              const configResponse = await fetch(`${API_URL}/timeline-items/${item.id}/generate-config/${language}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
              });

              if (configResponse.ok) {
                configSuccessCount++;
                console.log(`✅ Generated config for item ${item.id} in ${language}`);
              } else {
                const errorData = await configResponse.json();
                if (errorData.detail && errorData.detail.includes('already exists')) {
                  console.log(`ℹ️ Config for item ${item.id} in ${language} already exists`);
                  configSuccessCount++; // Count as success since it already exists
                } else if (errorData.detail && errorData.detail.includes('No original configuration found')) {
                  throw new Error(`Cannot generate ${languageDisplay} configuration: No original configuration found for timeline item ${item.id}. Please create original configurations first through the video editor.`);
                } else {
                  throw new Error(`Failed to generate config for item ${item.id} in ${languageDisplay}: ${errorData.detail || 'Unknown error'}`);
                }
              }
            } catch (error) {
              console.error(`Failed to generate config for item ${item.id} in ${language}:`, error);
              throw error; // Re-throw to stop the bulk generation process for non-outro items
            }
          }

          console.log(`✅ Configuration generation complete for ${languageDisplay}:`);
          console.log(`   - Successfully generated: ${configSuccessCount}`);
          console.log(`   - Total timeline items: ${timelineItems.length}`);
          console.log(`   - Success rate: ${Math.round((configSuccessCount/timelineItems.length) * 100)}%`);

          // Mark language as successful
          languageResult.success = true;

        } catch (languageError) {
          console.error(`❌ Error processing ${languageDisplay}:`, languageError);
          languageResult.errors.push(languageError.message);

          // Update current step to continue with the proper sequence
          currentStep = Math.ceil((results.length + 1) * 3); // Skip to next language steps
        }

        results.push(languageResult);
        console.log(`📊 Completed processing ${languageDisplay} - Success: ${languageResult.success}`);
      }

      // Final verification and results summary
      console.log(`🔍 Final verification: Processing completed for ${results.length} languages`);

      const successfulLanguages = results.filter(r => r.success);
      const failedLanguages = results.filter(r => !r.success);

      console.log(`📊 Bulk Generation Summary:`);
      console.log(`   - Successful: ${successfulLanguages.length}/${results.length}`);
      console.log(`   - Failed: ${failedLanguages.length}/${results.length}`);

      if (successfulLanguages.length > 0) {
        console.log(`✅ Successfully generated: ${successfulLanguages.map(r => r.language).join(', ')}`);
      }

      if (failedLanguages.length > 0) {
        console.log(`❌ Failed languages: ${failedLanguages.map(r => `${r.language} (${r.errors.join(', ')})`).join('; ')}`);
      }

      // Show appropriate success/partial success message
      if (successfulLanguages.length === results.length) {
        alert(`🎉 Bulk generation completed successfully!\n\nGenerated scripts, audio, and configurations for:\n${successfulLanguages.map(r => r.language).join('\n')}\n\nPage will refresh to show new data.`);
      } else if (successfulLanguages.length > 0) {
        alert(`⚠️ Bulk generation partially completed!\n\n✅ Successful: ${successfulLanguages.map(r => r.language).join(', ')}\n\n❌ Failed: ${failedLanguages.map(r => r.language).join(', ')}\n\nPage will refresh to show successful generations.`);
      } else {
        alert(`❌ Bulk generation failed for all languages!\n\nErrors:\n${failedLanguages.map(r => `${r.language}: ${r.errors.join(', ')}`).join('\n')}`);
      }

      // Refresh the page data to show the new languages (if any were successful)
      if (successfulLanguages.length > 0) {
        window.location.reload();
      }

    } catch (error) {
      console.error('Bulk generation system error:', error);
      alert(`❌ Bulk generation system error: ${error.message}`);
    } finally {
      setIsBulkGenerating(false);
      setBulkGenProgress({ step: '', current: 0, total: 0, currentLanguage: '' });
      setSelectedBulkLanguages([]);
    }
  }, [projectId, timelineItems, selectedBulkLanguages, selectedVoiceModel, languageNames]);
  
  // Calculate effective duration for current item
  const getEffectiveDuration = useCallback(async (item) => {
    if (!item) return 0;
    
    // Special handling for intro/outro videos
    if (item.type === 'intro' || item.type === 'outro') {
      try {
        const videoSource = item.type === 'intro' ? '/media/intro.mp4' : '/media/outro.mp4';
        const video = document.createElement('video');
        video.src = `${API_URL}${videoSource}`;
        const duration = await new Promise((resolve, reject) => {
          video.onloadedmetadata = () => {
            resolve(video.duration);
          };
          video.onerror = reject;
        });
        console.log(`🎬 ${item.type.toUpperCase()} video duration: ${duration}s`);
        return duration;
      } catch (error) {
        console.warn(`Failed to get ${item.type} video duration:`, error);
        return 10; // Default fallback
      }
    }
    
    const config = itemConfigurations.get(item.id);
    
    console.log(`🎯 DURATION DEBUG: Item ${item.id} (${item.startTime}s-${item.endTime}s)`);
    console.log(`   Has config:`, !!config);
    console.log(`   Config details:`, config);
    
    if (config) {
      // Has custom configuration - use saved timeline duration from config
      // Note: config is already the timeline object (stored as configData.configuration.timeline)
      const configDuration = config.duration;
      console.log(`   Config duration:`, configDuration);
      console.log(`   Calculated duration:`, item.endTime - item.startTime);
      
      if (configDuration) {
        console.log(`✅ Using saved duration from config for item ${item.id}: ${configDuration}s`);
        return configDuration;
      } else {
        // Fallback to calculated duration if no saved duration
        console.log(`❌ No saved duration in config for item ${item.id}, using calculated: ${item.endTime - item.startTime}s`);
        return item.endTime - item.startTime;
      }
    } else {
      // No custom configuration - use entire duration of longer media
      try {
        let videoDuration = 0;
        let audioDuration = 0;
        
        // Get video duration
        if (project?.videoUrl) {
          // Handle multi-video projects - use the first video for duration calculation
          let videoUrl = project.videoUrl;
          if (typeof videoUrl === 'string') {
            try {
              const parsed = JSON.parse(videoUrl);
              if (Array.isArray(parsed) && parsed.length > 0) {
                videoUrl = parsed[0]; // Use first video for duration
                console.log('📏 Using first video for duration calculation:', videoUrl);
              }
            } catch (e) {
              // Not JSON, use as-is
            }
          }

          const video = document.createElement('video');
          video.src = `${API_URL}${encodeURI(videoUrl)}`;
          await new Promise((resolve, reject) => {
            video.onloadedmetadata = () => {
              videoDuration = video.duration;
              resolve();
            };
            video.onerror = reject;
          });
        }
        
        // Get audio duration
        const audioSource = getAudioSource(item);
        if (audioSource) {
          const audio = document.createElement('audio');
          audio.src = `${API_URL}${audioSource}`;
          await new Promise((resolve, reject) => {
            audio.onloadedmetadata = () => {
              audioDuration = audio.duration;
              resolve();
            };
            audio.onerror = reject;
          });
        }
        
        // Return the longer of video or audio duration
        const maxDuration = Math.max(videoDuration, audioDuration, item.endTime - item.startTime);
        console.log(`Calculated effective duration for item ${item.id}: video=${videoDuration}s, audio=${audioDuration}s, clip=${item.endTime - item.startTime}s, using=${maxDuration}s`);
        return maxDuration;
        
      } catch (error) {
        console.warn('Error calculating effective duration:', error);
        return item.endTime - item.startTime; // Fallback to original clip duration
      }
    }
  }, [itemConfigurations, project, getAudioSource]);

  // Format time duration (seconds to minutes if > 60s)
  const formatDuration = useCallback((seconds) => {
    if (seconds >= 60) {
      const totalMinutes = seconds / 60;
      return `${totalMinutes.toFixed(2)}m`;
    } else {
      return `${seconds.toFixed(1)}s`;
    }
  }, []);

  // Calculate cumulative time ranges for all timeline items based on configuration durations
  const getCumulativeTimeRange = useCallback(async (targetItem, targetIndex) => {
    let cumulativeStart = 0;
    
    // Calculate start time by summing all previous items' durations
    for (let i = 0; i < targetIndex; i++) {
      const item = timelineItems[i];
      const config = itemConfigurations.get(item.id);
      
      if (config && config.duration) {
        // Use configuration duration if available
        cumulativeStart += config.duration;
      } else {
        // No configuration - return dash
        return { start: '-', end: '-' };
      }
    }
    
    // Get current item's duration
    const currentConfig = itemConfigurations.get(targetItem.id);
    if (currentConfig && currentConfig.duration) {
      const cumulativeEnd = cumulativeStart + currentConfig.duration;
      return {
        start: formatDuration(cumulativeStart),
        end: formatDuration(cumulativeEnd)
      };
    } else {
      // No configuration for current item
      return { start: '-', end: '-' };
    }
  }, [timelineItems, itemConfigurations, formatDuration]);

  // Calculate duration from start and end time strings
  const calculateDuration = useCallback((startStr, endStr) => {
    if (!startStr || !endStr) return null;
    
    // Helper function to convert time string to seconds
    const parseTimeString = (timeStr) => {
      if (timeStr.endsWith('s')) {
        return parseFloat(timeStr.replace('s', ''));
      } else if (timeStr.endsWith('m')) {
        return parseFloat(timeStr.replace('m', '')) * 60;
      }
      return parseFloat(timeStr);
    };
    
    const startSeconds = parseTimeString(startStr);
    const endSeconds = parseTimeString(endStr);
    
    if (isNaN(startSeconds) || isNaN(endSeconds)) return null;
    
    const durationSeconds = endSeconds - startSeconds;
    return formatDuration(durationSeconds);
  }, [formatDuration]);

  // Calculate and update cumulative time ranges for all items
  const updateCumulativeTimeRanges = useCallback(async () => {
    const newRanges = new Map();
    
    for (let i = 0; i < timelineItems.length; i++) {
      const item = timelineItems[i];
      const timeRange = await getCumulativeTimeRange(item, i);
      newRanges.set(item.id, timeRange);
    }
    
    setCumulativeTimeRanges(newRanges);
  }, [timelineItems, getCumulativeTimeRange]);

  // Update cumulative time ranges whenever configurations or timeline items change
  useEffect(() => {
    if (timelineItems.length > 0) {
      updateCumulativeTimeRanges();
    }
  }, [itemConfigurations, timelineItems, updateCumulativeTimeRanges]);

  // Get active video clip at current timeline time
  const getActiveVideoClip = useCallback((timelineTime) => {
    return currentVideoClips.find(clip => 
      timelineTime >= clip.startTime && timelineTime < clip.endTime
    );
  }, [currentVideoClips]);

  // Map timeline time to source video time based on active clip
  const getSourceVideoTime = useCallback((timelineTime) => {
    const activeClip = getActiveVideoClip(timelineTime);
    if (!activeClip) return 0;
    
    if (activeClip.type === 'frame') {
      // For frame clips, use the timestamp directly
      return activeClip.timestamp || 0;
    } else if (activeClip.type === 'video') {
      // For video clips, map timeline time to source time
      const timeInClip = timelineTime - activeClip.startTime;
      return (activeClip.sourceStartTime || 0) + timeInClip;
    }
    
    return 0;
  }, [getActiveVideoClip]);
  
  // Load current item's configuration
  const loadCurrentItemConfiguration = useCallback((item) => {
    if (!item) return;
    
    const config = itemConfigurations.get(item.id);
    
    if (config) {
      // Use saved configuration
      console.log(`Using saved configuration for item ${item.id}:`, config);
      console.log(`Shape layers:`, config.shapeLayers);
      console.log(`Text layers:`, config.textLayers);
      console.log(`Video clips:`, config.videoTrack);
      setCurrentVideoClips(config.videoTrack || []);
      setCurrentAudioClips(config.audioTrack || []);
      setCurrentShapeLayers(config.shapeLayers || []);
      setCurrentTextLayers(config.textLayers || []);
      // For Quick Preview, we use the original timeline item duration for playback
    } else {
      // Use default configuration
      console.log(`Using default configuration for item ${item.id}`);
      const defaultDuration = item.endTime - item.startTime;
      
      setCurrentVideoClips([{
        id: 'video-1',
        type: 'video',
        name: 'Video',
        startTime: 0,
        endTime: defaultDuration,
        videoOffset: item.startTime,
        source: 'video'
      }]);
      
      setCurrentAudioClips([]);
      setCurrentShapeLayers([]);
      setCurrentTextLayers([]);
    }
  }, [itemConfigurations, getAudioSource]);
  
  // Move to next timeline item
  const moveToNextItem = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    
    if (currentItemIndex < timelineItems.length - 1) {
      setCurrentItemIndex(prev => prev + 1);
    } else {
      // Finished all items
      setIsPlaying(false);
      setCurrentItemIndex(0);
    }
  }, [currentItemIndex, timelineItems.length]);
  
  
  // Helper function to show black frame with audio
  const showBlackFrameWithAudio = useCallback((item, video, audio, hasAudio, audioSource) => {
    console.log(`Showing black frame with audio for item ${item.id}`);
    
    // Properly clear video content to show black frame
    video.pause();
    video.currentTime = 0;
    
    // Clear the video source and load to remove previous frame
    video.src = '';
    video.load();
    
    // Set black background - this will be visible when video has no content
    video.style.backgroundColor = '#000000';
    video.style.display = 'block';
    
    // Additional fallback: set a black poster image
    video.poster = 'data:image/svg+xml;base64,' + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect width="100%" height="100%" fill="black"/></svg>');
    
    if (hasAudio && audio && audioSource) {
      audio.src = `${API_URL}${audioSource}`;
      audio.currentTime = 0;
      
      const audioPlayPromise = audio.play();
      if (audioPlayPromise) {
        audioPlayPromise.catch(error => {
          console.warn('Audio play failed in black frame mode:', error);
        });
      }
      
      // Move to next item when audio ends
      audio.onended = () => {
        moveToNextItem();
      };
      
      console.log(`Playing audio for item ${item.id} with black frame`);
    } else {
      // No audio, move to next item after delay based on item type
      const delay = item.type === 'frame' ? 3000 : 
                   item.type === 'clip' ? (item.endTime - item.startTime) * 1000 :
                   2000; // Default for intro/outro
      
      timeoutRef.current = setTimeout(() => {
        moveToNextItem();
      }, delay);
      
      console.log(`No audio available for item ${item.id}, showing black frame for ${delay}ms`);
    }
  }, [moveToNextItem]);
  
  // Play current timeline item (ClipEditor style)
  const playCurrentItem = useCallback(async () => {
    if (!currentItem || !project) return;
    
    console.log(`Playing item ${currentItem.id}`);
    
    const video = videoRef.current;
    const audio = audioRef.current;
    
    // Reset video styling
    if (video) {
      video.style.backgroundColor = '';
      video.style.display = '';
      video.removeAttribute('poster');
    }
    
    try {
      const hasVideo = project.videoUrl && project.videoUrl.trim() !== '';
      const audioSource = getAudioSource(currentItem);
      
      // Determine video source based on item type
      let videoSource = '';
      if (currentItem.type === 'intro') {
        videoSource = `${API_URL}/media/intro.mp4`;
        console.log(`🎬 INTRO detected - using intro.mp4`);
      } else if (currentItem.type === 'outro') {
        videoSource = `${API_URL}/media/outro.mp4`;
        console.log(`🎬 OUTRO detected - using outro.mp4`);
      } else if (hasVideo) {
        // Handle multi-video projects - get the correct video for this timeline item
        let videoUrl = project.videoUrl;

        // Check if this is a multi-video project
        if (typeof videoUrl === 'string') {
          try {
            const parsed = JSON.parse(videoUrl);
            if (Array.isArray(parsed) && parsed.length > 0) {
              // Multi-video project - find the correct video for this timeline item
              if (currentItem.video_url) {
                // Use clip-specific video
                videoUrl = currentItem.video_url;
                console.log(`🎬 Using clip-specific video:`, videoUrl);
              } else if (typeof currentItem.video_index === 'number' && parsed[currentItem.video_index]) {
                // Use video by index
                videoUrl = parsed[currentItem.video_index];
                console.log(`🎬 Using video at index ${currentItem.video_index}:`, videoUrl);
              } else {
                // Fallback to first video
                videoUrl = parsed[0];
                console.log(`🎬 Fallback to first video:`, videoUrl);
              }
            }
          } catch (e) {
            // Not JSON, use as-is (backward compatibility)
            console.log(`🎬 Using single video URL:`, videoUrl);
          }
        }

        videoSource = `${API_URL}${encodeURI(videoUrl)}`;
      }
      
      if (videoSource) {
        // Set up video source
        if (video.src !== videoSource) {
          video.src = videoSource;
          await new Promise((resolve, reject) => {
            video.onloadeddata = resolve;
            video.onerror = reject;
          });
        }
        
        // Initialize canvas rendering
        console.log(`Setting canvasReady to true for item ${currentItem.id}`);
        setCanvasReady(true);
        
        // Set initial video position
        if (currentItem.type === 'frame') {
          video.currentTime = currentItem.timestamp || 0;
          video.pause();
        } else if (currentItem.type === 'clip') {
          // For clips, set initial position and let sync handle the rest
          video.currentTime = currentItem.startTime || 0;
          setCurrentTime(0);
          
          // Audio will be automatically loaded via JSX src attribute
          // Just ensure it starts playing when needed
          if (audioSource && audio && isPlaying) {
            // The audio src is managed by the JSX element
            // Just trigger playback
            audio.play().catch(error => console.warn('Audio play failed:', error));
          }
        } else if (currentItem.type === 'intro' || currentItem.type === 'outro') {
          // Check if intro/outro has a saved configuration
          const config = itemConfigurations.get(currentItem.id);
          
          if (config) {
            // Has configuration - treat like clips with overlays
            video.currentTime = 0;
            setCurrentTime(0);
            console.log(`🎬 ${currentItem.type.toUpperCase()}: Starting clip-style playback with overlays`);
            
            // Initialize canvas rendering (same as clips)
            console.log(`Setting canvasReady to true for ${currentItem.type} item ${currentItem.id}`);
            setCanvasReady(true);
          } else {
            // No configuration - play video directly with built-in audio
            video.currentTime = 0;
            setCurrentTime(0);
            console.log(`🎬 ${currentItem.type.toUpperCase()}: Playing video directly with built-in audio (no overlays)`);
            
            // Play the video directly without overlays
            if (isPlaying) {
              try {
                await video.play();
                console.log(`🎬 ${currentItem.type.toUpperCase()}: Video playback started`);
                
                // Set up video end handler to move to next item
                video.onended = () => {
                  console.log(`🎬 ${currentItem.type.toUpperCase()}: Video ended, moving to next item`);
                  moveToNextItem();
                };
              } catch (error) {
                console.warn(`${currentItem.type} video play failed:`, error);
                // Move to next item if video fails to play
                setTimeout(() => moveToNextItem(), 1000);
              }
            }
          }
        }
        
      } else {
        // No video or audio - move to next item after delay
        const delay = currentItem.type === 'frame' ? 3000 : 
                     currentItem.type === 'clip' ? (currentItem.endTime - currentItem.startTime) * 1000 :
                     2000;
        
        timeoutRef.current = setTimeout(() => {
          moveToNextItem();
        }, delay);
      }
      
    } catch (error) {
      console.error('Error playing item:', error);
      moveToNextItem();
    }
  }, [currentItem, project, getAudioSource, showBlackFrameWithAudio, moveToNextItem, itemConfigurations]);
  
  // Start/stop playback
  const togglePlayback = useCallback(() => {
    if (isPlaying) {
      setIsPlaying(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (videoRef.current) videoRef.current.pause();
      if (audioRef.current) audioRef.current.pause();
    } else {
      setIsPlaying(true);
    }
  }, [isPlaying]);
  
  
  // Canvas rendering effect for live composition
  useEffect(() => {
    if (!canvasReady || !canvasRef.current || !videoRef.current) return;
    
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const ctx = canvas.getContext('2d');
    
    let animationFrame;
    
    const renderFrame = () => {
      // Continue rendering even when video is paused (to show held frame + overlays)
      if (!video) return;
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Get current video time relative to timeline
      const relativeTime = currentTime;
      
      // Draw video frame
      if (video.readyState >= 2) {
        try {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        } catch (error) {
          // Handle any drawing errors gracefully
          console.warn('Canvas drawing error:', error);
        }
      }
      
      // Draw shapes
      currentShapeLayers.forEach(shape => {
        if (relativeTime >= shape.startTime && relativeTime <= shape.endTime) {
          // Debug logging for shapes (reduced frequency to prevent blinking)
          if (shape.shapeType === 'arrow' && Math.floor(relativeTime * 2) !== Math.floor((relativeTime - 0.1) * 2)) {
            console.log(`Rendering arrow shape at time ${relativeTime}:`, shape);
          }
          
          // Calculate opacity based on fade timing (same logic as clip editor)
          let opacity = shape.opacity || 1;
          const timeSinceStart = relativeTime - shape.startTime;
          const timeUntilEnd = shape.endTime - relativeTime;
          
          if (shape.fadeInDuration && timeSinceStart < shape.fadeInDuration) {
            opacity = (timeSinceStart / shape.fadeInDuration) * (shape.opacity || 1);
          } else if (shape.fadeOutDuration && timeUntilEnd < shape.fadeOutDuration) {
            opacity = (timeUntilEnd / shape.fadeOutDuration) * (shape.opacity || 1);
          }
          
          ctx.save();
          
          // Apply opacity for fade effects
          ctx.globalAlpha = opacity;
          
          // Set up shape style
          if (shape.mode === 'outline') {
            ctx.strokeStyle = shape.stroke || '#ff0000';
            ctx.lineWidth = shape.strokeWidth || 3;
            ctx.fillStyle = 'transparent';
          } else if (shape.mode === 'fill') {
            ctx.fillStyle = shape.fill || '#ff0000';
          } else if (shape.mode === 'blur') {
            // Skip drawing blur shapes on canvas - they'll be handled by HTML overlay
            ctx.restore();
            return;
          }
          
          // Draw shape based on type with proper scaling
          const shapeType = shape.shapeType || 'rectangle';
          
          // Apply scale factors to match clip editor rendering
          const effectiveWidth = shape.width * (shape.scaleX || 1);
          const effectiveHeight = shape.height * (shape.scaleY || 1);
          
          // Debug logging for quick preview shapes (reduced frequency)
          if (Math.random() < 0.01) { // Log only 1% of the time to avoid spam
            console.log('🎬 QUICK PREVIEW RENDERING:', {
              id: shape.id,
              shapeType: shape.shapeType,
              originalWidth: shape.width,
              originalHeight: shape.height,
              scaleX: shape.scaleX || 1,
              scaleY: shape.scaleY || 1,
              effectiveWidth,
              effectiveHeight,
              x: shape.x,
              y: shape.y
            });
          }
          
          if (shapeType === 'rectangle') {
            if (shape.mode === 'outline') {
              ctx.strokeRect(shape.x, shape.y, effectiveWidth, effectiveHeight);
            } else {
              ctx.fillRect(shape.x, shape.y, effectiveWidth, effectiveHeight);
            }
          } else if (shapeType === 'circle') {
            ctx.beginPath();
            ctx.arc(shape.x + effectiveWidth/2, shape.y + effectiveHeight/2, effectiveWidth/2, 0, 2 * Math.PI);
            if (shape.mode === 'outline') {
              ctx.stroke();
            } else {
              ctx.fill();
            }
          } else if (shapeType === 'arrow' && arrowImage) {
            // Draw arrow using SVG image (match Konva positioning)
            // In Konva: x,y is the position, offsetX/Y centers the rotation
            const arrowWidth = shape.width || 280;
            const arrowHeight = shape.height || 120;
            
            ctx.save();
            
            // Move to shape position (shape.x, shape.y are the center point in Konva)
            ctx.translate(shape.x, shape.y);
            
            // Apply rotation if specified
            if (shape.rotation) {
              ctx.rotate((shape.rotation * Math.PI) / 180);
            }
            
            // Apply scaling if specified
            const scaleX = shape.scaleX || 1;
            const scaleY = shape.scaleY || 1;
            ctx.scale(scaleX, scaleY);
            
            // Draw the arrow image centered (like Konva's offsetX/offsetY behavior)
            ctx.drawImage(arrowImage, -arrowWidth/2, -arrowHeight/2, arrowWidth, arrowHeight);
            
            ctx.restore();
          }
          
          ctx.restore();
        }
      });
      
      // Draw text
      currentTextLayers.forEach(text => {
        if (relativeTime >= text.startTime && relativeTime <= text.endTime) {
          // Calculate opacity based on fade timing (same logic as clip editor)
          let opacity = text.opacity || 1;
          const timeSinceStart = relativeTime - text.startTime;
          const timeUntilEnd = text.endTime - relativeTime;
          
          if (text.fadeInDuration && timeSinceStart < text.fadeInDuration) {
            opacity = (timeSinceStart / text.fadeInDuration) * (text.opacity || 1);
          } else if (text.fadeOutDuration && timeUntilEnd < text.fadeOutDuration) {
            opacity = (timeUntilEnd / text.fadeOutDuration) * (text.opacity || 1);
          }
          
          ctx.save();
          ctx.globalAlpha = opacity;
          ctx.fillStyle = text.fill || '#ffffff';
          
          // Build font string with proper weight and style handling
          const fontSize = text.fontSize || 24;
          const fontFamily = text.fontFamily || getFontFamily(text.text, selectedLanguage, 'Arial');
          let fontWeight = text.fontWeight || 'normal';
          let fontStyle = text.fontStyle || 'normal';
          
          // Handle bold text - check both fontWeight and fontStyle properties
          if (fontStyle === 'bold' || fontWeight === 'bold') {
            fontWeight = 'bold';
            fontStyle = 'normal'; // Reset style since bold is a weight, not style
          }
          
          ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
          ctx.fillText(text.text, text.x, text.y + fontSize);
          ctx.restore();
        }
      });
      
      animationFrame = requestAnimationFrame(renderFrame);
    };
    
    renderFrame();
    
    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [canvasReady, currentTime, currentShapeLayers, currentTextLayers, arrowImage]);

  // Simple timeline-based playback timer (ClipEditor style)
  useEffect(() => {
    let intervalId;
    
    if (isPlaying && currentItem) {
      // For intro/outro without configuration, rely on video onended event instead of timer
      const config = itemConfigurations.get(currentItem.id);
      const useTimer = currentItem.type !== 'intro' && currentItem.type !== 'outro' || config;
      
      if (useTimer) {
        console.log(`Starting timeline playback timer for ${currentItem.type}`);
        intervalId = setInterval(() => {
          setCurrentTime(prevTime => {
            const newTime = prevTime + 0.1; // Update every 100ms
            const itemDuration = effectiveDuration || (currentItem.endTime - currentItem.startTime);
            
            // Check if we've reached the end of current item
            if (newTime >= itemDuration) {
              console.log(`${currentItem.type} completed: ${newTime.toFixed(2)}s >= ${itemDuration.toFixed(2)}s`);
              moveToNextItem();
              return 0; // Reset time for next item
            }
            
            return newTime;
          });
        }, 100);
      } else {
        console.log(`🎬 ${currentItem.type.toUpperCase()}: Using video onended event for timing (no configuration)`);
      }
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isPlaying, currentItem, effectiveDuration, moveToNextItem, itemConfigurations]);
  
  // Separate video/audio sync effect (ClipEditor style)
  useEffect(() => {
    if (!currentItem) return;
    if (!isPlaying) return;
    
    // Only use sync for clips and intro/outro WITH configurations
    const config = itemConfigurations.get(currentItem.id);
    const needsSync = currentItem.type === 'clip' || ((currentItem.type === 'intro' || currentItem.type === 'outro') && config);
    
    if (!needsSync) return;
    
    const video = videoRef.current;
    const audio = audioRef.current;
    
    if (!video) return;
    
    console.log(`🎬 SYNC: currentTime ${currentTime.toFixed(2)}s, isPlaying: ${isPlaying}`);
    
    const activeVideoClip = getActiveVideoClip(currentTime);
    
    // Handle video visibility and seeking
    if (activeVideoClip) {
      let actualVideoTime;
      
      if (activeVideoClip.type === 'frame') {
        // For frame clips, show the specific frame time
        actualVideoTime = activeVideoClip.frameTime || activeVideoClip.timestamp || 0;
        console.log(`Timeline ${currentTime.toFixed(2)}s: FRAME VISIBLE, showing frame at ${actualVideoTime.toFixed(2)}s`);
      } else {
        // For regular video clips, calculate time based on offset
        const timelineOffsetIntoClip = currentTime - activeVideoClip.startTime;
        const videoOffsetSeconds = activeVideoClip.videoOffset || activeVideoClip.sourceStartTime || 0;
        actualVideoTime = videoOffsetSeconds + timelineOffsetIntoClip;
        
        console.log(`Timeline ${currentTime.toFixed(2)}s: Video VISIBLE (clip ${activeVideoClip.startTime}-${activeVideoClip.endTime}), video offset: ${videoOffsetSeconds.toFixed(2)}s, playing video at ${actualVideoTime.toFixed(2)}s`);
      }
      
      // Show video and sync time
      video.style.opacity = '1';
      
      // Handle playback based on clip type
      if (activeVideoClip.type === 'frame') {
        // For frame clips, pause the video and show specific frame
        try {
          if (!video.paused) video.pause();
          if (Math.abs(video.currentTime - actualVideoTime) > 0.1) {
            video.currentTime = actualVideoTime;
          }
        } catch (error) {
          console.warn('Error pausing video for frame:', error);
        }
      } else {
        // For regular video clips, sync time and ensure video is playing
        const timeDifference = Math.abs(video.currentTime - actualVideoTime);
        if (timeDifference > 0.5) {
          console.log(`Video out of sync by ${timeDifference.toFixed(2)}s, seeking to ${actualVideoTime.toFixed(2)}s`);
          video.currentTime = actualVideoTime;
        }
        
        // Play the video if we're playing and it's paused
        if (isPlaying && video.paused) {
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              console.warn('Video play promise rejected:', error);
            });
          }
        }
      }
    } else {
      // Hide video (show black)
      console.log(`Timeline ${currentTime.toFixed(2)}s: Video HIDDEN (no active clip)`);
      video.style.opacity = '0';
    }
    
    // Handle audio playback
    const audioSource = getAudioSource(currentItem);
    if (audio && audioSource && audio.readyState >= 2) {
      if (!itemConfigurations.has(currentItem.id)) {
        // For clips without custom config, just ensure audio plays
        if (isPlaying && audio.paused) {
          audio.play().catch(error => console.warn('Audio play failed:', error));
        }
      } else {
        // For clips with custom config, sync audio with audio track
        const activeAudioClip = currentAudioClips.find(clip => 
          currentTime >= clip.startTime && currentTime < clip.endTime
        );
        
        if (activeAudioClip) {
          const timelineOffsetIntoClip = currentTime - activeAudioClip.startTime;
          const audioOffsetSeconds = activeAudioClip.audioOffset || 0;
          const actualAudioTime = audioOffsetSeconds + timelineOffsetIntoClip;
          
          if (actualAudioTime >= 0 && (isNaN(audio.duration) || actualAudioTime < audio.duration)) {
            const timeDifference = Math.abs(audio.currentTime - actualAudioTime);
            if (timeDifference > 0.5) {
              console.log(`Audio out of sync by ${timeDifference.toFixed(2)}s, seeking to ${actualAudioTime.toFixed(2)}s`);
              audio.currentTime = actualAudioTime;
            }
            
            if (isPlaying && audio.paused) {
              audio.play().catch(error => console.warn('Audio play failed:', error));
            }
          }
        } else {
          // No active audio clip, pause audio
          if (!audio.paused) {
            audio.pause();
          }
        }
      }
    }
  }, [currentTime, isPlaying, getActiveVideoClip, currentItem, getAudioSource, itemConfigurations, currentAudioClips]);
  
  
  // Load configuration when current item changes
  useEffect(() => {
    if (currentItem) {
      console.log(`[STATE RESET] Loading new item ${currentItem.id}, currentItemIndex=${currentItemIndex}`);
      loadCurrentItemConfiguration(currentItem);
      setCurrentTime(0); // Reset timeline time
      
      // Calculate and set effective duration
      getEffectiveDuration(currentItem).then(duration => {
        setEffectiveDuration(duration);
        console.log(`Set effective duration for item ${currentItem.id}: ${duration}s`);
      });
    }
  }, [currentItemIndex]); // Only depend on currentItemIndex to prevent multiple resets
  
  // Force audio reload when currentItem changes
  useEffect(() => {
    const audio = audioRef.current;
    if (audio && currentItem && getAudioSource(currentItem)) {
      console.log('Reloading audio source for new item:', getAudioSource(currentItem));
      audio.load(); // Force reload of audio element
    }
  }, [currentItem, getAudioSource]);
  
  // Play current item when playback starts or item changes
  useEffect(() => {
    if (isPlaying && currentItem) {
      playCurrentItem();
    }
  }, [isPlaying, currentItemIndex, playCurrentItem]);
  
  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  if (isLoading) {
    return (
      <div className="quick-preview-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading preview...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="quick-preview-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate(`/project/${projectId}`)} className="back-button">
            Back to Project
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="quick-preview-page">
      <header className="preview-header">
        <div className="header-content">
          <h1>Quick Preview - {project?.title}</h1>
          <p>Sequential playback of all timeline items</p>
        </div>
        <div className="header-actions">
          <button onClick={reloadConfigurations} className="back-button" style={{ marginRight: '10px' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
            </svg>
            Reload Config
          </button>
          <button onClick={() => navigate(`/project/${projectId}`)} className="back-button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
            </svg>
            Back to Project
          </button>
        </div>
      </header>
      
      <main className="preview-main">
        <div className="preview-top-controls">
          <div className="language-selector">
            <label htmlFor="preview-language">Language:</label>
            <select
              id="preview-language"
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="language-select"
              title="Choose language for video generation"
            >
              <option value="original">Original</option>
              {getAvailableLanguages().filter(lang => lang !== 'original').map(lang => (
                <option key={lang} value={lang}>
                  {getLanguageDisplayText(lang)}
                </option>
              ))}
            </select>
          </div>
          
          {/* Bulk Gen Script & Vo Button - show only for original language, hide on localhost */}
          {selectedLanguage === 'original' && !isLocalhost && (
            <div className="bulk-gen-button-container">
              <button
                onClick={() => {
                  // Auto-select specific languages if not already available
                  const targetLanguages = ['ta', 'te', 'de', 'hi', 'ar', 'id', 'it', 'ru', 'pt']; // Tamil, Telugu, German, Hindi, Arabic, Indonesian, Italian, Russian, Portuguese
                  const availableLanguages = getAvailableLanguages().filter(lang => lang !== 'original');
                  const languagesToSelect = targetLanguages.filter(lang => !availableLanguages.includes(lang));

                  console.log('🎯 Auto-selecting languages for bulk generation:');
                  console.log('Available languages:', availableLanguages.map(lang => languageNames[lang] || lang));
                  console.log('Target languages:', targetLanguages.map(lang => languageNames[lang] || lang));
                  console.log('Will auto-select:', languagesToSelect.map(lang => languageNames[lang] || lang));

                  setSelectedBulkLanguages(languagesToSelect);
                  setShowBulkGenModal(true);
                }}
                disabled={isBulkGenerating}
                className="bulk-gen-button"
                title="Generate AI scripts, audio, and configurations for multiple languages"
              >
                {isBulkGenerating ? (
                  <>
                    <svg className="spinning" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M14 2a10 10 0 0 0 0 20"/>
                    </svg>
                    {bulkGenProgress.step}...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 12l2 2 4-4"/>
                      <circle cx="12" cy="12" r="9"/>
                    </svg>
                    Bulk Gen Script & Vo
                  </>
                )}
              </button>
            </div>
          )}
          
          {/* Configuration Management Buttons - show for non-original languages, hide on localhost */}
          {selectedLanguage !== 'original' && currentItem && !isLocalhost && (
            <div className="config-management-buttons">
              <button
                onClick={handleGenerateConfiguration}
                disabled={isGeneratingConfig || timelineItems.every(item => configAvailability.get(`${item.id}-${selectedLanguage}`))}
                className="generate-config-button"
                title={
                  timelineItems.every(item => configAvailability.get(`${item.id}-${selectedLanguage}`))
                    ? `All items already have configurations for ${getLanguageDisplayText(selectedLanguage)}`
                    : `Generate configurations for all timeline items in ${getLanguageDisplayText(selectedLanguage)} language`
                }
              >
                {isGeneratingConfig ? (
                  <>
                    <svg className="spinning" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M14 2a10 10 0 0 0 0 20"/>
                    </svg>
                    Generating All...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 3h18v18H3zM12 8v8m-4-4h8"/>
                    </svg>
                    Generate All Configurations
                  </>
                )}
              </button>
              
              {timelineItems.some(item => configAvailability.get(`${item.id}-${selectedLanguage}`)) && (
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="delete-config-button"
                  title={`Delete configuration for ${getLanguageDisplayText(selectedLanguage)}`}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3,6 5,6 21,6"></polyline>
                    <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                  </svg>
                  Delete All Configurations
                </button>
              )}
            </div>
          )}
        </div>

        <div className="video-container" ref={canvasContainerRef} style={{ position: 'relative' }}>
          <video
            ref={videoRef}
            className="preview-video"
            crossOrigin="anonymous"
            muted={false}
            style={{ display: canvasReady ? 'none' : 'block' }}
          >
            Your browser does not support the video tag.
          </video>
          
          {/* Canvas overlay for live composition */}
          <canvas
            ref={canvasRef}
            width={1920}
            height={1080}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: canvasReady ? 'block' : 'none',
              objectFit: 'contain'
            }}
          />
          
          {/* Clickable clip indicator overlay */}
          {currentItem && (
            <div 
              className="clip-indicator-overlay"
              onClick={() => navigate(`/project/${projectId}/clip-editor/${currentItem.id}`)}
              title="Click to edit this clip"
              style={{
                position: 'absolute',
                top: '10px',
                left: '10px',
                background: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                zIndex: 10,
                transition: 'all 0.2s ease',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(0, 122, 204, 0.8)';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(0, 0, 0, 0.7)';
                e.target.style.transform = 'scale(1)';
              }}
            >
              {currentItem.type === 'frame' && `Frame ${currentItemIndex + 1}`}
              {currentItem.type === 'clip' && `Clip - ${currentItem.id}`}
              {currentItem.type === 'intro' && `Intro`}
              {currentItem.type === 'outro' && `Outro`}
            </div>
          )}
          
          {/* HTML-based blur overlay layer */}
          {currentShapeLayers.some(shape => 
            currentTime >= shape.startTime && 
            currentTime <= shape.endTime && 
            shape.mode === 'blur' &&
            (shape.shapeType === 'rectangle' || shape.shapeType === 'circle')
          ) && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 1
              }}
            >
              {currentShapeLayers.map(shape => {
                const isVisible = currentTime >= shape.startTime && currentTime <= shape.endTime;
                if (!isVisible || shape.mode !== 'blur') return null;
                
                const shapeType = shape.shapeType || 'rectangle';
                
                // Only support rectangle and circle for blur mode
                if (shapeType !== 'rectangle' && shapeType !== 'circle') return null;
                
                // Blur layers don't fade - they're either visible or not
                const opacity = shape.opacity || 1;
                
                // Calculate scaled dimensions to match canvas scaling
                const containerElement = canvasRef.current?.parentElement;
                if (!containerElement) return null;
                
                const containerRect = containerElement.getBoundingClientRect();
                const canvasScale = Math.min(containerRect.width / 1920, containerRect.height / 1080);
                
                const scaledX = shape.x * canvasScale;
                const scaledY = shape.y * canvasScale;
                // Apply scale factors to match clip editor rendering
                const effectiveWidth = shape.width * (shape.scaleX || 1);
                const effectiveHeight = shape.height * (shape.scaleY || 1);
                const scaledWidth = effectiveWidth * canvasScale;
                const scaledHeight = effectiveHeight * canvasScale;
                
                const commonStyles = {
                  position: 'absolute',
                  left: `${scaledX}px`,
                  top: `${scaledY}px`,
                  width: `${scaledWidth}px`,
                  height: `${scaledHeight}px`,
                  opacity: opacity,
                  transform: `rotate(${shape.rotation || 0}deg)`,
                  transformOrigin: 'center center',
                  backdropFilter: 'blur(4px)',
                  WebkitBackdropFilter: 'blur(4px)', // Safari support
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  pointerEvents: 'none'
                };
                
                if (shapeType === 'circle') {
                  return (
                    <div
                      key={`html-blur-${shape.id}`}
                      style={{
                        ...commonStyles,
                        borderRadius: '50%'
                      }}
                    />
                  );
                } else {
                  // Rectangle
                  return (
                    <div
                      key={`html-blur-${shape.id}`}
                      style={commonStyles}
                    />
                  );
                }
              })}
            </div>
          )}
          
          {/* Audio element for audio playback */}
          {currentItem && getAudioSource(currentItem) && (
            <audio
              ref={audioRef}
              src={`${API_URL}${getAudioSource(currentItem)}`}
              preload="metadata"
              crossOrigin="anonymous"
              onCanPlay={() => {
                console.log('Audio can play - ready for playback');
                const audio = audioRef.current;
                if (audio) {
                  console.log(`Audio duration: ${audio.duration}s, readyState: ${audio.readyState}`);
                }
              }}
              onLoadedData={() => console.log('Audio loaded data')}
              onLoadedMetadata={() => {
                console.log('Audio metadata loaded');
                const audio = audioRef.current;
                if (audio) {
                  console.log(`Audio duration: ${audio.duration}s, readyState: ${audio.readyState}`);
                }
              }}
              onError={(error) => {
                console.error('Audio loading error:', error);
                console.log('Failed audio source:', `${API_URL}${getAudioSource(currentItem)}`);
                const audio = audioRef.current;
                if (audio) {
                  console.log('Audio error details:', {
                    error: audio.error,
                    networkState: audio.networkState,
                    readyState: audio.readyState
                  });
                }
              }}
              onLoadStart={() => {
                console.log('Audio load started:', `${API_URL}${getAudioSource(currentItem)}`);
              }}
              onPlay={() => {
                console.log('Audio element started playing');
              }}
              onPause={() => {
                console.log('Audio element paused');
              }}
              onEnded={() => {
                console.log('Audio ended - moving to next item');
                // Only move to next item for clips without custom config
                if (!itemConfigurations.has(currentItem.id)) {
                  moveToNextItem();
                }
              }}
            />
          )}
        </div>
        
        <div className="preview-controls">
          <button onClick={togglePlayback} className="play-button">
            {isPlaying ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
            )}
            {isPlaying ? 'Pause' : 'Play'}
          </button>
          
          <div className="navigation-controls">
            <button 
              onClick={() => setCurrentItemIndex(Math.max(0, currentItemIndex - 1))}
              disabled={currentItemIndex === 0}
              className="nav-button"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
              </svg>
              Previous
            </button>
            <button 
              onClick={() => setCurrentItemIndex(Math.min(timelineItems.length - 1, currentItemIndex + 1))}
              disabled={currentItemIndex === timelineItems.length - 1}
              className="nav-button"
            >
              Next
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
              </svg>
            </button>
          </div>
          
          {/* Render controls - show only on localhost */}
          {isLocalhost && (
            <div className="render-controls-group">
              <select
                id="render-speed"
                value={renderSpeed}
                onChange={(e) => setRenderSpeed(e.target.value)}
                className="speed-select-integrated"
                title="Choose render speed vs quality balance"
              >
                <option value="fast">Fast</option>
                <option value="balanced">Balanced</option>
                <option value="high_quality">High Quality</option>
              </select>

              {/* Test Mode Switch */}
              <div className="test-mode-switch" title="Enable test mode to show clip names on rendered video">
                <label>
                  <input
                    type="checkbox"
                    checked={testMode}
                    onChange={(e) => setTestMode(e.target.checked)}
                  />
                  Test Mode
                </label>
              </div>

              <button
                onClick={handleGenerateVideos}
                className={`render-button-integrated ${isGeneratingVideos ? 'rendering' : ''}`}
                disabled={isGeneratingVideos}
                title="Render videos for all edited clips"
              >
                {isGeneratingVideos ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="spinning">
                      <path d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z"/>
                    </svg>
                    {generationProgress.phase ? (
                      <>
                        {generationProgress.phase}
                        <br />
                        <small>{generationProgress.current}/{generationProgress.total}</small>
                      </>
                    ) : (
                      `Rendering ${generationProgress.current}/${generationProgress.total}`
                    )}
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17,10.5V7A1,1 0 0,0 16,6H4A1,1 0 0,0 3,7V17A1,1 0 0,0 4,18H16A1,1 0 0,0 17,17V13.5L21,17.5V6.5L17,10.5Z"/>
                    </svg>
                    Render
                  </>
                )}
              </button>
            </div>
          )}

          {/* Rendered Videos button - always visible */}
          <button
            onClick={() => window.location.href = `/project/${projectId}/videos`}
            className="rendered-videos-button"
            title="View all rendered videos for this project"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-collection-play-fill" viewBox="0 0 16 16">
              <path d="M2.5 3.5a.5.5 0 0 1 0-1h11a.5.5 0 0 1 0 1h-11zm2-2a.5.5 0 0 1 0-1h7a.5.5 0 0 1 0 1h-7zM0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V7zm6 3.5a.5.5 0 0 1 .8-.4l2 1.33a.5.5 0 0 1 0 .84l-2 1.33A.5.5 0 0 1 6 13.5v-3z"/>
            </svg>
            Rendered Videos
          </button>

          {/* Deploy button - always visible */}
          <button
            onClick={() => window.location.href = `/project/${projectId}/deploy`}
            className="deploy-button"
            title="Deploy this project"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-cloud-upload-fill" viewBox="0 0 16 16">
              <path fillRule="evenodd" d="M8 0a5.53 5.53 0 0 0-3.594 1.342c-.766.66-1.321 1.52-1.464 2.383C1.266 4.095 0 5.555 0 7.318 0 9.366 1.708 11 3.781 11H7.5V5.707L5.354 7.854a.5.5 0 1 1-.708-.708l3-3a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 5.707V11h4.188C14.502 11 16 9.57 16 7.773c0-1.636-1.242-2.969-2.834-3.194C12.923 1.999 10.69 0 8 0z"/>
            </svg>
            Deploy
          </button>

          {/* Bulk Render button - show only on localhost */}
          {isLocalhost && (
            <button
              onClick={handleBulkRender}
              disabled={isBulkRendering || isGeneratingVideos}
              className={`bulk-render-button-right ${isBulkRendering ? 'bulk-rendering' : ''}`}
              title={`Render videos for all ${getAvailableLanguages().length - 1} available languages`}
            >
              {isBulkRendering ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="spinning">
                    <path d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z"/>
                  </svg>
                  {bulkRenderProgress.currentLanguage ? (
                    <>
                      {bulkRenderProgress.currentLanguage}
                      <br />
                      <small>{bulkRenderProgress.current}/{bulkRenderProgress.total}</small>
                    </>
                  ) : (
                    `Bulk Rendering ${bulkRenderProgress.current}/${bulkRenderProgress.total}`
                  )}
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17,10.5V7A1,1 0 0,0 16,6H4A1,1 0 0,0 3,7V17A1,1 0 0,0 4,18H16A1,1 0 0,0 17,17V13.5L21,17.5V6.5L17,10.5Z"/>
                    <path d="M8,9H12V11H8V9M8,13H14V15H8V13Z"/>
                  </svg>
                  Bulk Render
                </>
              )}
            </button>
          )}
        </div>
        
        <div className="timeline-items-list">
          <h3>Timeline Items</h3>
          <div className="items-grid">
            {timelineItems.map((item, index) => (
              <div 
                key={item.id} 
                className={`timeline-item-card ${index === currentItemIndex ? 'current' : ''} ${
                  selectedLanguage === 'original' 
                    ? (hasCustomConfig(item) ? 'has-generated-video' : '')
                    : (hasCustomConfig(item) ? 'has-config-available' : 'has-config-missing')
                }`}
                onClick={() => setCurrentItemIndex(index)}
              >
                <div className="item-number">{index + 1}</div>
                <div className="item-details">
                  <div className="item-type">
                    {item.type === 'clip' ? `Clip - ${item.id}` : item.type}
                  </div>
                  {item.type === 'frame' && (
                    <div className="item-time">@{item.timestamp?.toFixed(2)}s</div>
                  )}
                  {item.type === 'clip' && (
                    <div className="item-time">
                      {cumulativeTimeRanges.get(item.id) ? 
                        (() => {
                          const range = cumulativeTimeRanges.get(item.id);
                          const duration = calculateDuration(range.start, range.end);
                          return `${range.start} - ${range.end}${duration ? ` (${duration})` : ''}`;
                        })() : 
                        '- - -'
                      }
                    </div>
                  )}
                  {item.rawText && (
                    <div className="item-text">{item.rawText.substring(0, 50)}...</div>
                  )}
                </div>
                <button
                  className="edit-item-button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(`/project/${projectId}/clip-editor/${item.id}`);
                  }}
                  title="Edit this clip"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      </main>
      
      {/* Delete Configuration Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Delete All Configurations</h3>
            <p>
              Are you sure you want to delete <strong>ALL</strong> configurations for <strong>{getLanguageDisplayText(selectedLanguage)}</strong> language across all timeline items?
            </p>
            <p className="modal-warning">
              This will delete configurations for ALL clips/items that have {getLanguageDisplayText(selectedLanguage)} configurations. This action cannot be undone. Original language configurations will remain intact.
            </p>
            <div className="modal-buttons">
              <button 
                onClick={() => setShowDeleteModal(false)}
                className="modal-cancel-button"
                disabled={isDeletingConfig}
              >
                Cancel
              </button>
              <button 
                onClick={handleDeleteConfiguration}
                className="modal-delete-button"
                disabled={isDeletingConfig}
              >
                {isDeletingConfig ? (
                  <>
                    <svg className="spinning" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M14 2a10 10 0 0 0 0 20"/>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  'Delete All Configurations'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Generation Modal */}
      {showBulkGenModal && (
        <div className="modal-overlay" onClick={() => setShowBulkGenModal(false)}>
          <div className="modal-content bulk-gen-modal" onClick={e => e.stopPropagation()}>
            <h3>Bulk Generate Script & Audio</h3>
            <p>Generate AI scripts, audio, and configurations for multiple languages at once.</p>
            
            <div className="bulk-gen-form">
              {/* Language Selection */}
              <div className="form-group">
                <label>Select Languages:</label>
                <div className="language-checkboxes">
                  {Object.entries(languageNames).map(([code, name]) => (
                    <label key={code} className="checkbox-label">
                      <input
                        type="checkbox"
                        value={code}
                        checked={selectedBulkLanguages.includes(code)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedBulkLanguages(prev => [...prev, code]);
                          } else {
                            setSelectedBulkLanguages(prev => prev.filter(lang => lang !== code));
                          }
                        }}
                      />
                      <span className="checkbox-text">{name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Voice Model Selection */}
              <div className="form-group">
                <label htmlFor="voice-model">Voice Model:</label>
                <select
                  id="voice-model"
                  value={selectedVoiceModel}
                  onChange={(e) => setSelectedVoiceModel(e.target.value)}
                  className="voice-model-select"
                >
                  <option value="google_tts">Google TTS</option>
                  <option value="elevenlabs">ElevenLabs</option>
                </select>
              </div>

              {/* Progress Display */}
              {isBulkGenerating && (
                <div className="progress-display">
                  <div className="progress-info">
                    <strong>{bulkGenProgress.step}</strong>
                    {bulkGenProgress.currentLanguage && (
                      <span> for {bulkGenProgress.currentLanguage}</span>
                    )}
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${(bulkGenProgress.current / bulkGenProgress.total) * 100}%` }}
                    ></div>
                  </div>
                  <div className="progress-text">
                    {bulkGenProgress.current} / {bulkGenProgress.total}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-buttons">
              <button 
                onClick={() => setShowBulkGenModal(false)}
                className="modal-cancel-button"
                disabled={isBulkGenerating}
              >
                Cancel
              </button>
              <button 
                onClick={handleBulkGeneration}
                className="modal-confirm-button"
                disabled={isBulkGenerating || selectedBulkLanguages.length === 0}
              >
                {isBulkGenerating ? (
                  <>
                    <svg className="spinning" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M14 2a10 10 0 0 0 0 20"/>
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 12l2 2 4-4"/>
                      <circle cx="12" cy="12" r="9"/>
                    </svg>
                    Generate ({selectedBulkLanguages.length} languages)
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Render Confirmation Modal */}
      <BulkRenderModal
        isOpen={showBulkRenderModal}
        onClose={() => setShowBulkRenderModal(false)}
        onConfirm={executeBulkRender}
        projectId={projectId}
        languages={getAvailableLanguages()}
      />

      {/* Bulk Render Success Modal */}
      <BulkRenderSuccessModal
        isOpen={showBulkSuccessModal}
        onClose={handleBulkSuccessModalClose}
        session={bulkSuccessData?.session}
        uploadResult={bulkSuccessData?.uploadResult}
      />
    </div>
  );
}

export default QuickPreviewPage;